import { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  IconButton,
  FormHelperText,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import CategoryTreeSelect from "./components/CategoryTreeSelect";
import TopicTreeSelect from "./components/TopicTreeSelect";
import {
  Save,
  ArrowBack,
  Delete,
  Add,
  AutoFixHigh,
  AutoAwesome,
  PhotoLibrary,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Sync,
  CloudUpload,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Description as DescriptionIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  QUILL_MODULES,
  QUILL_FORMATS,
  QuillR2Editor,
  QUILL_MODULES as EXPL_QUILL_MODULES,
  QUILL_FORMATS as EXPL_QUILL_FORMATS,
  PASSAGE_QUILL_MODULES,
  PASSAGE_QUILL_FORMATS,
} from "../../utils/quillQuestionEditorConfig";
import { insertLineNumberMarkers, stripLineNumberMarkers } from "./utils/passageLineExtract";
import { toast } from "react-toastify";
import api from "../../api";
import GenerateVisualButton from "../../components/GenerateVisualButton";
import QuestionTypeSelector from "./components/QuestionTypeSelector";
import McqOptions from "./components/McqOptions";
import TrueFalseOptions from "./components/TrueFalseOptions";
import FillInBlankEditor from "./components/FillInBlankEditor";
import AnswerVariantsEditor from "./components/AnswerVariantsEditor";
import FillTypeMarksControls from "./components/FillTypeMarksControls";
import TableFillEditor, { DEFAULT_GRID } from "./components/TableFillEditor";
import ImageOverlayFillEditor from "./components/ImageOverlayFillEditor";
import MatchingEditor from "./components/MatchingEditor";
import OrderingEditor from "./components/OrderingEditor";
import EssayAnswer from "./components/EssayAnswer";
import ShortAnswerEditor from "./components/ShortAnswerEditor";
import VideoExplanationPanel from "./components/VideoExplanationPanel";
import DiagramHubSpoke from "./components/DiagramHubSpoke";
import DiagramHubSpokeOuter from "./components/DiagramHubSpokeOuter";
import HtmlWithLatex from "./components/HtmlWithLatex";
import QuestionPaperLayout from "./components/QuestionPaperLayout";
import InteractiveDiagramEditor, { UnifySharedGridsControl, SharedGridPartsEditor } from "./components/InteractiveDiagramEditor";
import { describeMeasurementAutoDetect } from "../../utils/measurementQuestionDetect";
import ImportMarkdownViewerDialog from "./components/ImportMarkdownViewerDialog";

const normalizeBlanks = (blanks) =>
  Array.isArray(blanks) ? blanks.map((b) => (Array.isArray(b) ? b : [b])) : null;

const stripTagsKeepLatex = (html) => (html || "").replace(/<[^>]*>/g, "");

const flattenCategoryTree = (tree) => {
  const flat = [];
  const walk = (nodes) => nodes.forEach((n) => {
    flat.push(n);
    if (n.children?.length) walk(n.children);
    if (n.all_children?.length) walk(n.all_children);
  });
  walk(Array.isArray(tree) ? tree : []);
  return flat;
};

const findCategoryIdByName = (tree, name) => {
  const flat = flattenCategoryTree(tree);
  const found = flat.find((n) => String(n.name || "").trim().toLowerCase() === name.toLowerCase());
  return found ? String(found.id) : null;
};

const stripToPlainText = (html) => {
  let text = (html || "").replace(/<[^>]*>/g, "");
  if (text.includes("&")) {
    const el = document.createElement("textarea");
    el.innerHTML = text;
    text = el.value;
  }
  text = text
    .replace(/\$\$([^$]*)\$\$/g, "$1")
    .replace(/\$([^$\n]*)\$/g, "$1")
    .replace(/\\ /g, " ")
    .replace(/\\[a-zA-Z]+\{([^}]*)\}/g, "$1")
    .replace(/\\[a-zA-Z]+/g, "");
  return text;
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatExplanationStepwise = (raw) => {
  const clean = String(raw || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\r/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "";

  const explicitLines = String(raw || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const hasStepMarkers = explicitLines.some((l) => /^(\d+[).]|step\s*\d+[:.)-]?)/i.test(l));

  let steps = [];
  if (hasStepMarkers) {
    steps = explicitLines.map((l) =>
      l.replace(/^(\d+[).]\s*|step\s*\d+[:.)-]?\s*)/i, "").trim()
    );
  } else {
    steps = clean
      .split(/(?:\.\s+|;\s+|,\s+(?=(?:then|next|after that|finally|so)\b))/i)
      .map((s) => (s == null ? "" : String(s)).trim())
      .filter((s) => s.length > 0 && !/^(then|next|after that|finally|so)$/i.test(s));
  }

  if (steps.length === 0) steps = [clean];
  if (steps.length === 1 && steps[0].length > 140) {
    steps = steps[0]
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const items = steps
    .map((s) => escapeHtml(s.replace(/\s+/g, " ").trim()))
    .filter(Boolean)
    .map((s) => `<li>${s}</li>`)
    .join("");

  return items ? `<ol>${items}</ol>` : "";
};

const normalizeQuillValue = (html) => {
  if (!html) return "";
  const compact = String(html).trim();
  if (compact === "<p><br></p>" || compact === "<p></p>") return "";
  return compact;
};

const toRoman = (n) => {
  const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
  const syms = ['m','cm','d','cd','c','xc','l','xl','x','ix','v','iv','i'];
  let r = '';
  vals.forEach((v, i) => { while (n >= v) { r += syms[i]; n -= v; } });
  return r;
};

export const sumSubQuestionMarks = (subQuestions) =>
  (subQuestions || []).reduce((sum, sq) => sum + (parseInt(sq.default_marks, 10) || 0), 0);

export const buildSubQuestionAiPayload = (sq, meta = {}) => {
  const difficulty = meta.difficulty ?? "Medium";
  const topic = meta.topic ?? null;
  const t = sq.type;
  const base = {
    type: t,
    question_text: sq.question_text || "",
    difficulty,
    default_marks: parseInt(sq.default_marks, 10) || 1,
    topic,
    image_description: null,
    image_url: sq.image_url || null,
    image_urls: sq.image_url ? [sq.image_url] : null,
    section_title: null,
    section_instructions: null,
    explanation: sq.explanation || null,
  };
  switch (t) {
    case "multiple-choice": {
      const opts = (sq.options || []).map((o, i) => ({
        text: o.text || "",
        option_text: o.text || "",
        image_url: o.image_url || null,
        is_correct: i === sq.correct_option_index,
      }));
      return {
        ...base,
        options: opts,
        answer_data: { correct_options: [sq.correct_option_index ?? 0] },
      };
    }
    case "multiple-select": {
      const idxs = sq.correct_option_indices || [];
      const opts = (sq.options || []).map((o, i) => ({
        text: o.text || "",
        option_text: o.text || "",
        image_url: o.image_url || null,
        is_correct: idxs.includes(i),
      }));
      return {
        ...base,
        options: opts,
        answer_data: { correct_options: [...idxs] },
      };
    }
    case "true-false": {
      const opts = [
        { text: "True", option_text: "True", is_correct: sq.trueFalseAnswer },
        { text: "False", option_text: "False", is_correct: !sq.trueFalseAnswer },
      ];
      return {
        ...base,
        options: opts,
        answer_data: { correct_options: [sq.trueFalseAnswer ? 0 : 1] },
      };
    }
    case "fill-in-the-blank":
    case "multi-fill":
      return {
        ...base,
        answer_data: {
          correct_blanks: sq.blanks || [[""]],
          answer_variants: sq.answerVariants?.length ? sq.answerVariants : [],
          ...(sq.blankLabels?.length > 0 ? { blank_labels: sq.blankLabels } : {}),
          ...(sq.blankMarks?.length > 0 ? { blank_marks: sq.blankMarks } : {}),
          ...(sq.blankLabelPositions?.length > 0 ? { blank_label_positions: sq.blankLabelPositions } : {}),
          ...(sq.marksMode === "fixed" ? { marks_mode: "fixed", marks_total: parseInt(sq.fixedTotal, 10) || 1 } : {}),
        },
      };
    case "table":
      return {
        ...base,
        answer_data: {
          correct_blanks: sq.blanks || [[""]],
          answer_variants: sq.answerVariants?.length ? sq.answerVariants : [],
          ...(sq.blankLabels?.length > 0 ? { blank_labels: sq.blankLabels } : {}),
          ...(sq.blankMarks?.length > 0 ? { blank_marks: sq.blankMarks } : {}),
          ...(sq.tableLayout?.grid?.length > 0 ? { table_layout: sq.tableLayout } : {}),
          ...(sq.overlayLayout?.rows_interchangeable ? { overlay_layout: { rows_interchangeable: true, ...(sq.overlayLayout?.interchangeable_row_indices?.length ? { interchangeable_row_indices: sq.overlayLayout.interchangeable_row_indices } : {}) } } : {}),
          ...(sq.marksMode === "fixed" ? { marks_mode: "fixed", marks_total: parseInt(sq.fixedTotal, 10) || 1 } : {}),
        },
      };
    case "image-fill":
      return {
        ...base,
        answer_data: {
          correct_blanks: sq.blanks || [[""]],
          answer_variants: sq.answerVariants?.length ? sq.answerVariants : [],
          ...(sq.blankLabels?.length > 0 ? { blank_labels: sq.blankLabels } : {}),
          ...(sq.blankMarks?.length > 0 ? { blank_marks: sq.blankMarks } : {}),
          ...((sq.overlayLayout?.image_url || sq.overlayLayout?.mode === "inline" || sq.overlayLayout?.blanks_interchangeable) ? { overlay_layout: sq.overlayLayout } : {}),
          ...(sq.marksMode === "fixed" ? { marks_mode: "fixed", marks_total: parseInt(sq.fixedTotal, 10) || 1 } : {}),
        },
      };
    case "short-answer":
      return {
        ...base,
        answer_data: { correct_answer_text: sq.shortAnswer || "" },
      };
    case "essay":
      return {
        ...base,
        answer_data: { correct_answer_text: sq.modelAnswer || "", model_answer: sq.modelAnswer || "" },
      };
    case "matching":
      return {
        ...base,
        options: (sq.options || []).map((o) => ({
          text: o.text || "",
          option_text: o.text || "",
          image_url: o.image_url || null,
          match_text: o.match_text || "",
          is_correct: true,
        })),
        answer_data: {},
      };
    case "ordering":
      return {
        ...base,
        options: (sq.options || []).map((o) => ({
          text: o.text || "",
          option_text: o.text || "",
          image_url: o.image_url || null,
          is_correct: true,
        })),
        answer_data: {},
      };
    default:
      return base;
  }
};

// The AI rewrites the whole question at once, but a reviewer often wants only part of it — the
// re-worded stem but not the regenerated options, or just the explanation. Rather than teach every
// apply* function about partial accepts, fold the unselected sections back to their original values
// and hand the apply functions a normal-looking suggestion object.
export const AI_ACCEPT_SECTIONS = {
  question: ['question_text', 'section_title', 'section_instructions', 'image_description', 'image_url', 'image_urls', 'difficulty', 'default_marks'],
  answer: ['options', 'answer_data'],
  explanation: ['explanation'],
};

export const mergeAiSuggestion = (original, suggestion, accepted) => {
  if (!suggestion || typeof suggestion !== 'object') return suggestion;
  const merged = { ...suggestion };
  Object.entries(AI_ACCEPT_SECTIONS).forEach(([section, keys]) => {
    if (accepted?.[section]) return;
    keys.forEach((key) => {
      // Drop the key when the original had none, so the apply functions' `!= null` guards skip it
      // instead of writing an empty value over what the editor already has.
      if (original && original[key] !== undefined) merged[key] = original[key];
      else delete merged[key];
    });
  });
  return merged;
};

export const AI_ACCEPT_DEFAULT = { question: true, answer: true, explanation: true };

// AI Improve returns the explanation as one unbroken blob — unlike the explanation-only "AI Generate"
// button, which has always run its text through formatExplanationStepwise. Break it into the same
// numbered steps so the reviewer can actually read it before accepting. Text that already carries
// block markup (an authored list, paragraphs, or an embedded image) is left alone — re-formatting it
// would strip the tags and lose the image.
export const formatAiExplanation = (raw) => {
  const s = String(raw ?? "");
  if (!s.trim()) return s;
  if (/<(?:p|ol|ul|li|br|img|table|h[1-6])\b/i.test(s)) return s;
  return formatExplanationStepwise(s);
};

// Format once, where the suggestion arrives — so the compare preview and the accepted value are the
// same text, rather than the reviewer approving a blob that silently changes shape on apply.
export const withFormattedExplanation = (imp) =>
  imp && typeof imp === "object" && imp.explanation != null
    ? { ...imp, explanation: formatAiExplanation(imp.explanation) }
    : imp;

// QuestionPaperLayout renders the stem, options and answer but not the explanation, so the compare
// dialog showed nothing for it — you could tick "Explanation" without seeing what you were accepting.
export const AiExplanationPreview = ({ html }) => {
  const text = String(html ?? "").replace(/<[^>]*>/g, "").trim();
  return (
    <>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.5 }}>
        Explanation
      </Typography>
      {text ? (
        <Box
          sx={{
            fontSize: "0.875rem",
            "& p": { m: 0, mb: 0.5 },
            // CssBaseline strips list markers/indent, so the stepwise <ol> would render as an
            // unnumbered run-on — exactly the blob this formatting exists to avoid.
            "& ol, & ul": { listStyle: "revert", pl: 3, m: 0 },
            "& li": { display: "list-item", mb: 0.75, lineHeight: 1.5 },
          }}
        >
          <HtmlWithLatex html={html} />
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: "text.disabled", fontStyle: "italic" }}>
          No explanation
        </Typography>
      )}
    </>
  );
};

export const AiAcceptSections = ({ value, onChange, showAnswer = true, showExplanation = true }) => {
  const items = [
    ['question', 'Question text'],
    ...(showAnswer ? [['answer', 'Answer & options']] : []),
    ...(showExplanation ? [['explanation', 'Explanation']] : []),
  ];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
        Accept:
      </Typography>
      {items.map(([key, label]) => (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              size="small"
              checked={!!value[key]}
              onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
            />
          }
          label={<Typography variant="body2">{label}</Typography>}
        />
      ))}
    </Box>
  );
};

export const applyImprovedSubQuestionPatch = (imp) => {
  if (!imp || typeof imp !== "object") return {};
  const patch = {};
  if (imp.question_text != null) patch.question_text = imp.question_text;
  if (imp.default_marks != null) patch.default_marks = imp.default_marks;
  if (imp.explanation != null) patch.explanation = imp.explanation;
  if (imp.image_url !== undefined) patch.image_url = imp.image_url || "";
  const ad = imp.answer_data || {};
  const opts = imp.options || [];
  const impType = imp.type;
  if (impType === "multiple-choice" && opts.length > 0) {
    patch.options = opts.map((o) => ({
      text: o.text || o.option_text || "",
      image_url: o.image_url || "",
    }));
    const ci = opts.findIndex((o) => o.is_correct);
    if (ci >= 0) patch.correct_option_index = ci;
    else if (Array.isArray(ad.correct_options) && ad.correct_options.length > 0) {
      patch.correct_option_index = ad.correct_options[0];
    }
  }
  if (impType === "multiple-select" && opts.length > 0) {
    patch.options = opts.map((o) => ({
      text: o.text || o.option_text || "",
      image_url: o.image_url || "",
    }));
    const idxs = opts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), []);
    if (idxs.length > 0) patch.correct_option_indices = idxs;
    else if (Array.isArray(ad.correct_options)) patch.correct_option_indices = ad.correct_options;
  }
  if (impType === "true-false") {
    const trueOpt = opts.find((o) => (o.text || o.option_text) === "True");
    if (trueOpt && trueOpt.is_correct !== undefined) patch.trueFalseAnswer = !!trueOpt.is_correct;
  }
  if (impType === "fill-in-the-blank" || impType === "multi-fill") {
    if (ad.correct_blanks?.length > 0) patch.blanks = normalizeBlanks(ad.correct_blanks);
    if (ad.blank_labels?.length > 0) patch.blankLabels = ad.blank_labels;
    if (ad.blank_marks?.length > 0) patch.blankMarks = ad.blank_marks;
    if (ad.blank_label_positions?.length > 0) patch.blankLabelPositions = ad.blank_label_positions;
  }
  if (impType === "short-answer" && ad.correct_answer_text) patch.shortAnswer = ad.correct_answer_text;
  if (impType === "essay" && ad.correct_answer_text) patch.modelAnswer = ad.correct_answer_text;
  if (impType === "matching" && opts.length > 0) {
    patch.options = opts.map((o) => ({
      text: o.text || o.option_text || "",
      match_text: o.match_text || "",
      image_url: o.image_url || "",
    }));
  }
  if (impType === "ordering" && opts.length > 0) {
    patch.options = opts.map((o) => ({
      text: o.text || o.option_text || "",
      image_url: o.image_url || "",
    }));
  }
  return patch;
};

export const mapAnswerDataToSubQuestionPatch = (answerData, sqType, sq) => {
  const patch = {};
  if (!answerData || typeof answerData !== "object") return patch;
  switch (sqType) {
    case "multiple-choice":
      if (answerData.correct_options?.length > 0) {
        patch.correct_option_index = answerData.correct_options[0];
      }
      break;
    case "multiple-select":
      if (answerData.correct_options?.length > 0) {
        patch.correct_option_indices = answerData.correct_options;
      }
      break;
    case "true-false":
      if (answerData.correct_options !== undefined) {
        patch.trueFalseAnswer = (answerData.correct_options?.[0] ?? 0) === 0;
      }
      break;
    case "fill-in-the-blank":
    case "multi-fill":
      if (answerData.correct_blanks?.length > 0) {
        patch.blanks = normalizeBlanks(answerData.correct_blanks);
      }
      if (answerData.blank_labels?.length > 0) patch.blankLabels = answerData.blank_labels;
      if (answerData.blank_marks?.length > 0) patch.blankMarks = answerData.blank_marks;
      if (answerData.blank_label_positions?.length > 0) patch.blankLabelPositions = answerData.blank_label_positions;
      break;
    case "short-answer":
      if (answerData.correct_answer_text) {
        patch.shortAnswer = answerData.correct_answer_text;
      }
      break;
    case "essay":
      if (answerData.correct_answer_text) {
        patch.modelAnswer = answerData.correct_answer_text;
      }
      break;
    case "matching":
      if (answerData.correct_matches?.length > 0) {
        const prev = sq.options || [];
        patch.options = prev.map((opt) => {
          const match = answerData.correct_matches.find(
            (m) =>
              m.left &&
              opt.text &&
              m.left.trim().toLowerCase() === opt.text.trim().toLowerCase()
          );
          return match ? { ...opt, match_text: match.right || "" } : opt;
        });
      }
      break;
    case "ordering":
      if (answerData.correct_ordering_items?.length > 0) {
        const orderedTexts = answerData.correct_ordering_items;
        const prev = sq.options || [];
        patch.options = orderedTexts.map((text) => {
          const found = prev.find(
            (o) => o.text.trim().toLowerCase() === text.trim().toLowerCase()
          );
          return found || { text };
        });
      }
      break;
    default:
      break;
  }
  return patch;
};

// ─── Hub-spoke detection helpers ─────────────────────────────────────────────
const BLANK_PH = ["□", "[ ]", "[]", "__", "___"];

// Lenient: all labels contain a blank placeholder (hub value may be supplied separately)
const hasAllBlanks = (labels) => {
  if (!Array.isArray(labels) || labels.length < 2) return false;
  return labels.every((l) => BLANK_PH.some((ph) => String(l).includes(ph)));
};

// True when diagram type is any hub-spoke variant (used for shared UI sections)
const isAnyHubSpoke = (diagramType, labels) =>
  diagramType === "hub-spoke-outer" ||
  diagramType === "hub-spoke" ||
  hasAllBlanks(labels);

// ─── Helper: build initial state object for a sub-question ────────────────────
const buildSubQuestionState = (sq = {}) => {
  const opts = sq.options || [];
  const ans = sq.answer || {};
  const type = sq.type || "multiple-choice";

  const base = {
    _key: sq.id ? `sq-${sq.id}` : `new-${Math.random().toString(36).slice(2)}`,
    id: sq.id,
    question_text: sq.question_text || "",
    type,
    image_url: sq.image_url || "",
    image_description: sq.image_description ?? "",
    default_marks: sq.default_marks ?? 1,
    options: [{ text: "", image_url: "" }, { text: "", image_url: "" }],
    correct_option_index: 0,
    correct_option_indices: [],
    trueFalseAnswer: true,
    blanks: type === "multi-fill" ? [[""], [""]] : [[""]],
    blankLabels: [],
    blankMarks: [],
    blankLabelPositions: [],
    tableLayout: ans.table_layout?.grid ? ans.table_layout : DEFAULT_GRID(),
    overlayLayout: (ans.overlay_layout?.image_url || ans.overlay_layout?.mode || ans.overlay_layout?.rows_interchangeable || ans.overlay_layout?.blanks_interchangeable) ? ans.overlay_layout : { image_url: "", blanks: [] },
    marksMode: ans.marks_mode === "fixed" ? "fixed" : "sum",
    fixedTotal: ans.marks_total ?? (sq.default_marks ?? 1),
    exactMatch: !!ans.exact_match,
    modelAnswer: "",
    shortAnswer: "",
    correctSolutionImageUrl: ans.correct_solution_image_url || "",
    interactiveSpec: (ans.solution_geometry && ans.solution_geometry.interactive) || null,
    explanation: sq.explanation || "",
    hide_shared_context: !!sq.hide_shared_context,
    mcq_recommended: !!sq.mcq_recommended,
    mcq_recommended_note: sq.mcq_recommended_note || "",
    sub_questions: (sq.sub_questions || []).map((ssq) => buildSubQuestionState(ssq)),
  };

  switch (type) {
    case "multiple-choice":
      base.options = opts.map((o) => ({
        text: o.option_text || o.text || "",
        image_url: o.image_url || "",
      }));
      base.correct_option_index = Math.max(0, opts.findIndex((o) => o.is_correct));
      break;
    case "multiple-select":
      base.options = opts.map((o) => ({
        text: o.option_text || o.text || "",
        image_url: o.image_url || "",
      }));
      base.correct_option_indices = opts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), []);
      break;
    case "true-false": {
      const trueOpt = opts.find((o) => o.option_text === "True");
      base.trueFalseAnswer = trueOpt?.is_correct ?? true;
      break;
    }
    case "fill-in-the-blank":
    case "multi-fill":
      base.blanks = normalizeBlanks(ans.correct_blanks) || (type === "multi-fill" ? [[""], [""]] : [[""]] );
      base.answerVariants = ans.answer_variants || null;
      base.blankLabels = ans.blank_labels || [];
      base.blankMarks = ans.blank_marks || [];
      base.blankLabelPositions = ans.blank_label_positions || [];
      break;
    case "table":
      base.blanks = normalizeBlanks(ans.correct_blanks) || [[""]];
      base.answerVariants = ans.answer_variants || null;
      base.blankLabels = ans.blank_labels || [];
      base.blankMarks = ans.blank_marks || [];
      base.tableLayout = ans.table_layout?.grid ? ans.table_layout : DEFAULT_GRID();
      // overlay_layout carries rows_interchangeable ("the rows may be written in any order") for a table
      // too — it has no image overlay, but the flag lives on the same column.
      base.overlayLayout = ans.overlay_layout?.rows_interchangeable ? ans.overlay_layout : { image_url: "", blanks: [] };
      break;
    case "image-fill":
      base.blanks = normalizeBlanks(ans.correct_blanks) || [[""]];
      base.answerVariants = ans.answer_variants || null;
      base.blankLabels = ans.blank_labels || [];
      base.blankMarks = ans.blank_marks || [];
      base.overlayLayout = (ans.overlay_layout?.image_url || ans.overlay_layout?.mode || ans.overlay_layout?.rows_interchangeable || ans.overlay_layout?.blanks_interchangeable) ? ans.overlay_layout : { image_url: "", blanks: [] };
      break;
    case "matching":
      base.options = opts.map((o) => ({
        text: o.option_text || o.text || "",
        match_text: o.match_text || "",
        image_url: o.image_url || "",
      }));
      break;
    case "ordering":
      base.options = opts.map((o) => ({
        text: o.option_text || o.text || "",
        image_url: o.image_url || "",
      }));
      break;
    case "essay":
      base.modelAnswer = ans.correct_answer_text || "";
      break;
    case "short-answer":
      base.shortAnswer = ans.correct_answer_text || "";
      break;
    case "drawing":
      base.shortAnswer = ans.correct_answer_text || "";
      base.correctSolutionImageUrl = ans.correct_solution_image_url || "";
      break;
    default:
      break;
  }
  return base;
};

const defaultSubQuestion = () => buildSubQuestionState({ type: "fill-in-the-blank" });

// QUILL_MODULES / QUILL_FORMATS: shared stable ref — see utils/quillQuestionEditorConfig.js

// Optional drawing/shade answer attached to an image-fill question (e.g. fill the table cells AND
// shade the bar for the missing match). The drawing usually sits on a DIFFERENT image than the fill
// overlay, so the admin picks which attached image it draws on. Persisted as solution_geometry.interactive.
const CombinedDrawingAnswer = ({ interactiveSpec, setInteractiveSpec, attachedImages = [] }) => {
  const enabled = !!interactiveSpec;
  const kind = interactiveSpec?.kind || "shade";
  const image = interactiveSpec?.image_url || "";
  return (
    <Box sx={{ border: "1px dashed #cfcfcf", borderRadius: 1, p: 1.5, mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormControlLabel
        control={<Checkbox size="small" checked={enabled} onChange={(e) => setInteractiveSpec(e.target.checked ? { kind: "shade", image_url: attachedImages[0] || "" } : null)} />}
        label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Also add a drawing/shade answer (e.g. “draw the bar” on the chart) — graded as an extra mark</Typography>}
      />
      {enabled ? (
        <>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Drawing mode</InputLabel>
              <Select label="Drawing mode" value={kind} onChange={(e) => setInteractiveSpec({ ...interactiveSpec, kind: e.target.value })}>
                <MenuItem value="shade">Shade regions (tap to fill)</MenuItem>
                <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                <MenuItem value="polygon">Draw shape(s) by area (triangle / square / N rectangles)</MenuItem>
                <MenuItem value="segments">Join points (segments)</MenuItem>
                <MenuItem value="marker">Arrow / bar marker (value on axis)</MenuItem>
              </Select>
            </FormControl>
            {attachedImages.length > 1 ? (
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Draw on which image</InputLabel>
                <Select label="Draw on which image" value={image} onChange={(e) => setInteractiveSpec({ ...interactiveSpec, image_url: e.target.value })}>
                  {attachedImages.map((u, i) => (
                    <MenuItem key={u} value={u}>Image {i + 1}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Box>
          <InteractiveDiagramEditor
            kind={kind}
            spec={interactiveSpec}
            onChange={setInteractiveSpec}
            imageUrl={image || attachedImages[0] || ""}
          />
        </>
      ) : null}
    </Box>
  );
};

// ─── SortableSubQuestionItem ──────────────────────────────────────────────────
const SortableSubQuestionItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 0.25, cursor: "grab", color: "#bdbdbd", "&:hover": { color: "#666" }, userSelect: "none" }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon sx={{ fontSize: 16, transform: "rotate(90deg)" }} />
      </Box>
      {children}
    </div>
  );
};

// ─── SubQuestionEditor ─────────────────────────────────────────────────────────
const SubQuestionEditor = ({
  sq,
  sqIndex,
  label,
  onChange,
  onDelete,
  parentQuestionId,
  parentImages = [],
  isEdit,
  parentDifficulty,
  parentTopic,
  fixedMarksLabel,
}) => {
  const fileRef = useRef(null);
  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [improving, setImproving] = useState(false);
  const [generatingAnswer, setGeneratingAnswer] = useState(false);
  const [generatingAnswerImage, setGeneratingAnswerImage] = useState(false);
  const [subAiImproveOpen, setSubAiImproveOpen] = useState(false);
  const [subAiOriginal, setSubAiOriginal] = useState(null);
  const [subAiSuggestion, setSubAiSuggestion] = useState(null);
  const [subAiAccept, setSubAiAccept] = useState(AI_ACCEPT_DEFAULT);
  const [subStemPreview, setSubStemPreview] = useState(false);

  const canUseSubAi = Boolean(isEdit && parentQuestionId);
  const subAiMeta = { difficulty: parentDifficulty, topic: parentTopic };

  const uploadImageFile = async (file) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setIsImageProcessing(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post("/admin/question-bank/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ image_url: res.data?.data?.image_url || "" });
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setIsImageProcessing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadImageFile(file);
  };

  const handleRemoveImage = async () => {
    if (!sq.image_url) return;
    setIsImageProcessing(true);
    const prevUrl = sq.image_url;
    onChange({ image_url: "" });
    try {
      await api.post("/admin/question-bank/delete-image", { url: prevUrl });
      toast.success("Image removed");
    } catch {
      onChange({ image_url: prevUrl });
      toast.error("Failed to remove image");
    } finally {
      setIsImageProcessing(false);
    }
  };

  const handleTypeChange = (newType) => {
    const tableDefaults = newType === "table"
      ? {
          blanks: Array.from({ length: 9 }, () => [""]),
          blankMarks: Array.from({ length: 9 }, () => 1),
          tableLayout: DEFAULT_GRID(),
        }
      : {};
    const imageFillDefaults = newType === "image-fill"
      ? {
          blanks: [[""]],
          blankMarks: [1],
          overlayLayout: { image_url: "", blanks: [] },
        }
      : {};
    onChange({
      type: newType,
      options: [{ text: "", image_url: "" }, { text: "", image_url: "" }],
      correct_option_index: 0,
      correct_option_indices: [],
      trueFalseAnswer: true,
      blanks: newType === "multi-fill" ? [[""], [""]] : [[""]],
      blankLabels: [],
      blankMarks: [],
      blankLabelPositions: [],
      modelAnswer: "",
      shortAnswer: "",
      ...tableDefaults,
      ...imageFillDefaults,
    });
  };

  const handleSubGenerateExplanation = async () => {
    if (!canUseSubAi) {
      toast.error("Save the grouped question first to use AI on sub-questions.");
      return;
    }
    setGeneratingExplanation(true);
    try {
      const res = await api.post(`/admin/question-bank/${parentQuestionId}/generate-explanation`, {
        question: buildSubQuestionAiPayload(sq, subAiMeta),
      });
      const text = res.data?.data?.explanation;
      if (text) {
        const prev = sq.explanation || "";
        const merged =
          /<img\s/i.test(prev) && prev.replace(/<[^>]*>/g, "").trim()
            ? `${prev}<p><br></p>${formatExplanationStepwise(text)}`
            : formatExplanationStepwise(text);
        onChange({ explanation: merged });
      }
      toast.success("Explanation generated in step-wise format");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate explanation");
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const handleSubAiImprove = async () => {
    if (!canUseSubAi) {
      toast.error("Save the grouped question first to use AI on sub-questions.");
      return;
    }
    setImproving(true);
    try {
      const payload = buildSubQuestionAiPayload(sq, subAiMeta);
      const res = await api.post(`/admin/question-bank/${parentQuestionId}/ai-improve-question`, {
        question: payload,
      });
      const imp = res.data?.data?.improved_question;
      setSubAiOriginal(payload);
      setSubAiSuggestion(withFormattedExplanation(imp));
      setSubAiAccept(AI_ACCEPT_DEFAULT);
      setSubAiImproveOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "AI improvement failed. Please try again.");
    } finally {
      setImproving(false);
    }
  };

  const handleSubGenerateAnswerImage = async () => {
    if (!sq.id) {
      toast.info("Save once so this sub-question is stored, then generate the answer diagram.");
      return;
    }
    setGeneratingAnswerImage(true);
    try {
      const res = await api.post(`/admin/question-bank/${sq.id}/generate-answer-image`, {
        image_url: sq.image_url || null,
      });
      const url = res.data?.data?.correct_solution_image_url || "";
      if (url) {
        onChange({ correctSolutionImageUrl: url });
        toast.success("Answer diagram generated. Review and save if correct.");
      } else {
        toast.error("No answer image was returned.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate answer diagram.");
    } finally {
      setGeneratingAnswerImage(false);
    }
  };

  const handleSubGenerateAnswer = async () => {
    if (!canUseSubAi) {
      toast.error("Save the grouped question first to use AI on sub-questions.");
      return;
    }
    if (!sq.id) {
      toast.info("Save once so this sub-question is stored, then you can generate answers with AI.");
      return;
    }
    setGeneratingAnswer(true);
    try {
      const res = await api.post(`/admin/question-bank/${sq.id}/generate-answer`, {
        question: buildSubQuestionAiPayload(sq, subAiMeta),
      });
      const answerData = res.data?.data?.answer_data || {};
      const hasImage = res.data?.data?.has_image;
      const patch = mapAnswerDataToSubQuestionPatch(answerData, sq.type, sq);
      if (Object.keys(patch).length > 0) onChange(patch);
      const provider = hasImage ? "GPT-4o (vision)" : "AI";
      toast.success(`Answer generated using ${provider}. Review and save if correct.`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate answer. Please try again.");
    } finally {
      setGeneratingAnswer(false);
    }
  };

  const renderAnswerEditor = () => {
    switch (sq.type) {
      case "multiple-choice":
        return (
          <McqOptions
            options={sq.options}
            setOptions={(opts) => onChange({ options: opts })}
            correctIndex={sq.correct_option_index}
            setCorrectIndex={(idx) => onChange({ correct_option_index: idx })}
          />
        );
      case "multiple-select":
        return (
          <McqOptions
            options={sq.options}
            setOptions={(opts) => onChange({ options: opts })}
            correctIndices={sq.correct_option_indices}
            setCorrectIndices={(idxs) => onChange({ correct_option_indices: idxs })}
            isMultiSelect
          />
        );
      case "true-false":
        return <TrueFalseOptions value={sq.trueFalseAnswer} onChange={(v) => onChange({ trueFalseAnswer: v })} />;
      case "fill-in-the-blank":
      case "multi-fill":
        return (
          <>
            <FillTypeMarksControls
              marksMode={sq.marksMode}
              onMarksModeChange={(mode) => onChange({ marksMode: mode })}
              fixedTotal={sq.fixedTotal}
              onFixedTotalChange={(v) => onChange({ fixedTotal: v })}
              blankCount={sq.blanks?.length || 0}
              showRowsInterchangeable
              rowsInterchangeable={sq.overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => onChange({ overlayLayout: { ...sq.overlayLayout, rows_interchangeable: v } })}
              interchangeableRowIndices={sq.overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => onChange({ overlayLayout: { ...sq.overlayLayout, interchangeable_row_indices: v } })}
              exactMatch={sq.exactMatch}
              onExactMatchChange={(v) => onChange({ exactMatch: v })}
            />
            <FillInBlankEditor blanks={sq.blanks} setBlanks={(bl) => onChange({ blanks: bl })} isMulti={sq.type === "multi-fill"} blankLabels={sq.blankLabels} setBlankLabels={(l) => onChange({ blankLabels: l })} blankMarks={sq.blankMarks} setBlankMarks={(m) => onChange({ blankMarks: m })} blankLabelPositions={sq.blankLabelPositions} setBlankLabelPositions={(p) => onChange({ blankLabelPositions: p })} fillMarksMode={sq.marksMode} fillFixedTotal={sq.fixedTotal} />
            <AnswerVariantsEditor
              variants={sq.answerVariants}
              setVariants={(v) => onChange({ answerVariants: v })}
              blankCount={sq.blanks?.length || 0}
              blankLabels={sq.blankLabels}
            />
            <CombinedDrawingAnswer
              interactiveSpec={sq.interactiveSpec}
              setInteractiveSpec={(s) => onChange({ interactiveSpec: s })}
              attachedImages={[...(sq.image_url ? [sq.image_url] : []), ...parentImages].filter((u, i, a) => u && a.indexOf(u) === i)}
            />
          </>
        );
      case "table":
        return (
          <>
            <FillTypeMarksControls
              marksMode={sq.marksMode}
              onMarksModeChange={(mode) => onChange({ marksMode: mode })}
              fixedTotal={sq.fixedTotal}
              onFixedTotalChange={(v) => onChange({ fixedTotal: v })}
              blankCount={sq.blanks?.length || 0}
              showRowsInterchangeable
              rowsInterchangeable={sq.overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => onChange({ overlayLayout: { ...sq.overlayLayout, rows_interchangeable: v } })}
              interchangeableRowIndices={sq.overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => onChange({ overlayLayout: { ...sq.overlayLayout, interchangeable_row_indices: v } })}
              exactMatch={sq.exactMatch}
              onExactMatchChange={(v) => onChange({ exactMatch: v })}
            />
            <TableFillEditor blanks={sq.blanks} setBlanks={(bl) => onChange({ blanks: bl })} blankMarks={sq.blankMarks} setBlankMarks={(m) => onChange({ blankMarks: m })} blankLabels={sq.blankLabels} setBlankLabels={(l) => onChange({ blankLabels: l })} tableLayout={sq.tableLayout} setTableLayout={(t) => onChange({ tableLayout: t })} showMathTools />
            <AnswerVariantsEditor
              variants={sq.answerVariants}
              setVariants={(v) => onChange({ answerVariants: v })}
              blankCount={sq.blanks?.length || 0}
              blankLabels={sq.blankLabels}
            />
          </>
        );
      case "image-fill":
        return (
          <>
            <FillTypeMarksControls
              marksMode={sq.marksMode}
              onMarksModeChange={(mode) => onChange({ marksMode: mode })}
              fixedTotal={sq.fixedTotal}
              onFixedTotalChange={(v) => onChange({ fixedTotal: v })}
              blankCount={sq.blanks?.length || 0}
              showRowsInterchangeable
              rowsInterchangeable={sq.overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => onChange({ overlayLayout: { ...sq.overlayLayout, rows_interchangeable: v } })}
              interchangeableRowIndices={sq.overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => onChange({ overlayLayout: { ...sq.overlayLayout, interchangeable_row_indices: v } })}
              blanksInterchangeable={sq.overlayLayout?.blanks_interchangeable}
              onBlanksInterchangeableChange={(v) => onChange({ overlayLayout: { ...sq.overlayLayout, blanks_interchangeable: v } })}
              exactMatch={sq.exactMatch}
              onExactMatchChange={(v) => onChange({ exactMatch: v })}
            />
            <ImageOverlayFillEditor blanks={sq.blanks} setBlanks={(bl) => onChange({ blanks: bl })} blankMarks={sq.blankMarks} setBlankMarks={(m) => onChange({ blankMarks: m })} blankLabels={sq.blankLabels} setBlankLabels={(l) => onChange({ blankLabels: l })} overlayLayout={sq.overlayLayout} setOverlayLayout={(o) => onChange({ overlayLayout: o })} attachedImages={[...(sq.image_url ? [sq.image_url] : []), ...parentImages].filter((u, i, a) => u && a.indexOf(u) === i)} />
            <AnswerVariantsEditor
              variants={sq.answerVariants}
              setVariants={(v) => onChange({ answerVariants: v })}
              blankCount={sq.blanks?.length || 0}
              blankLabels={sq.blankLabels}
            />
            <CombinedDrawingAnswer
              interactiveSpec={sq.interactiveSpec}
              setInteractiveSpec={(s) => onChange({ interactiveSpec: s })}
              attachedImages={[...(sq.image_url ? [sq.image_url] : []), ...parentImages].filter((u, i, a) => u && a.indexOf(u) === i)}
            />
          </>
        );
      case "matching":
        return <MatchingEditor options={sq.options} setOptions={(opts) => onChange({ options: opts })} />;
      case "ordering":
        return <OrderingEditor options={sq.options} setOptions={(opts) => onChange({ options: opts })} />;
      case "essay":
        return <EssayAnswer value={sq.modelAnswer} onChange={(v) => onChange({ modelAnswer: v })} showMathTools />;
      case "short-answer":
        return (
          <>
            <FormControlLabel
              sx={{ mb: 1, display: "block" }}
              control={<Checkbox checked={!!sq.exactMatch} onChange={(e) => onChange({ exactMatch: e.target.checked })} size="small" />}
              label={
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
                  Answer must be written exactly like this (no near-enough matching, no AI)
                </Typography>
              }
            />
            <ShortAnswerEditor value={sq.shortAnswer} onChange={(v) => onChange({ shortAnswer: v })} showMathTools />
          </>
        );
      case "drawing": {
        const subDrawMode = sq.interactiveSpec?.kind || "free";
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControl size="small" sx={{ maxWidth: 320 }}>
              <InputLabel>Answer mode</InputLabel>
              <Select
                label="Answer mode"
                value={subDrawMode}
                // Re-selecting the SAME mode must be a no-op — otherwise it replaces the spec with a bare
                // { kind }, wiping an existing marker/points/… question's axis, targets and settings. Keep
                // the image the spec already draws on when the admin genuinely switches modes.
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === subDrawMode) return;
                  if (next === "free") { onChange({ interactiveSpec: null }); return; }
                  onChange({ interactiveSpec: { kind: next, ...(sq.interactiveSpec?.image_url ? { image_url: sq.interactiveSpec.image_url } : {}) } });
                }}
              >
                <MenuItem value="free">Free draw (AI vision)</MenuItem>
                <MenuItem value="shade">Shade regions (tap to fill)</MenuItem>
                <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                <MenuItem value="cut-drag">Cut shape &amp; drag pieces (rearrange)</MenuItem>
                <MenuItem value="segments">Join the crosses (nodes → lines)</MenuItem>
                <MenuItem value="polygon">Draw shape by area (triangle / square)</MenuItem>
                <MenuItem value="glyph">Place / reflect / rotate letters</MenuItem>
                <MenuItem value="drag-shape">Drag-copy shape (translation)</MenuItem>
                <MenuItem value="reflect">Reflect shape (auto-computed)</MenuItem>
                <MenuItem value="rotate">Rotate shape (auto-computed)</MenuItem>
                <MenuItem value="clock">Clock hands (set the time)</MenuItem>
                <MenuItem value="marker">Arrow / bar marker (value on axis)</MenuItem>
                <MenuItem value="pictogram">Pictogram (complete the row of icons)</MenuItem>
              </Select>
            </FormControl>
            {subDrawMode === "free" ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Free draw: graded against a generated answer diagram via AI vision. Interactive modes
                  grade instantly with no AI.
                </Typography>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>Answer description (optional)</Typography>
                  <Box sx={{ '& .ql-toolbar': { borderRadius: '6px 6px 0 0' }, '& .ql-container': { minHeight: 90, borderRadius: '0 0 6px 6px' } }}>
                    <QuillR2Editor
                      value={sq.shortAnswer || ""}
                      onChange={(html) => onChange({ shortAnswer: normalizeQuillValue(html) })}
                      modules={EXPL_QUILL_MODULES}
                      formats={EXPL_QUILL_FORMATS}
                      placeholder="e.g. Reflect the triangle across the line; vertices mirror onto the opposite side. Add an image of the worked solution if helpful."
                      style={{ minHeight: 110 }}
                      showMathTools
                    />
                  </Box>
                </Box>
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={generatingAnswerImage ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                    onClick={handleSubGenerateAnswerImage}
                    disabled={generatingAnswerImage || !sq.id}
                    sx={{ textTransform: "none" }}
                  >
                    {generatingAnswerImage ? "Generating…" : "Generate Answer Diagram"}
                  </Button>
                </Box>
                {sq.correctSolutionImageUrl ? (
                  <Box sx={{ display: "inline-flex", position: "relative", alignSelf: "flex-start" }}>
                    <img
                      src={sq.correctSolutionImageUrl}
                      alt="Correct answer diagram"
                      style={{ maxWidth: 280, maxHeight: 220, borderRadius: 4, border: "1px solid #e0e0e0", objectFit: "contain" }}
                      onError={(e) => { e.target.style.opacity = "0.3"; }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => onChange({ correctSolutionImageUrl: "" })}
                      sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(255,255,255,0.85)", color: "#f44336", p: 0.25 }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    Attach the sub-question image and save, then click Generate.
                  </Typography>
                )}
              </>
            ) : (
              <InteractiveDiagramEditor
                kind={subDrawMode}
                spec={sq.interactiveSpec}
                onChange={(spec) => onChange({ interactiveSpec: spec })}
                imageUrl={sq.image_url || ""}
              />
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
    <Paper
      elevation={0}
      sx={{ p: 2.5, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2, background: "#fafafa" }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={`(${label})`}
            size="small"
            sx={{ fontWeight: 700, bgcolor: "#3f2b96", color: "#fff", fontSize: "0.8rem" }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333" }}>
            Sub-question {sqIndex + 1}
            {sq.id && (
              <Typography component="span" variant="caption" sx={{ color: "#999", ml: 1 }}>
                #{sq.id}
              </Typography>
            )}
          </Typography>
        </Box>
        <IconButton size="small" color="error" onClick={onDelete} title="Remove sub-question">
          <Delete fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        {/* Type selector */}
        <Grid item xs={12} sm={6}>
          <QuestionTypeSelector value={sq.type} onChange={(e) => handleTypeChange(e.target.value)} />
        </Grid>
        {/* Marks */}
        <Grid item xs={12} sm={3}>
          {fixedMarksLabel ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
              <Typography variant="caption" sx={{ color: "#888" }}>Marks:</Typography>
              <Chip label={fixedMarksLabel} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
            </Box>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Marks"
              type="number"
              value={sq.default_marks}
              onChange={(e) => onChange({ default_marks: e.target.value })}
              inputProps={{ min: 1 }}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={!!sq.hide_shared_context}
                onChange={(e) => onChange({ hide_shared_context: e.target.checked })}
                size="small"
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
                Hide shared passage/images for this part (standalone question)
              </Typography>
            }
          />
          <FormControlLabel
            sx={{ m: 0, ml: 2 }}
            control={
              <Checkbox
                checked={!!sq.mcq_recommended}
                onChange={(e) => onChange({ mcq_recommended: e.target.checked })}
                size="small"
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
                Recommend converting this part to MCQ (hard to answer online)
              </Typography>
            }
          />
          {!!sq.mcq_recommended && (
            <TextField
              size="small"
              fullWidth
              label="Note shown to students (optional)"
              value={sq.mcq_recommended_note || ""}
              onChange={(e) => onChange({ mcq_recommended_note: e.target.value })}
              inputProps={{ maxLength: 500 }}
              sx={{ mt: 0.5 }}
            />
          )}
        </Grid>

        {/* Sub-question text */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#444" }}>
              Sub-question Text *
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {canUseSubAi && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={improving ? <CircularProgress size={12} /> : <AutoFixHigh sx={{ fontSize: "0.9rem !important" }} />}
                  onClick={handleSubAiImprove}
                  disabled={improving || generatingExplanation}
                  sx={{ py: 0.25, px: 1, fontSize: "0.75rem", textTransform: "none" }}
                >
                  {improving ? "Working…" : "AI Improve"}
                </Button>
              )}
              <IconButton size="small" onClick={() => setSubStemPreview((v) => !v)} title="Toggle live preview">
                {subStemPreview ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              "& .quill": { background: "#fff", borderRadius: 1 },
              "& .ql-container": {
                minHeight: "120px",
                fontSize: "14px",
                borderRadius: "0 0 4px 4px",
              },
              "& .ql-toolbar": {
                borderRadius: "4px 4px 0 0",
                background: "#f9f9f9",
                flexWrap: "wrap",
              },
              "& .ql-editor": { minHeight: "120px" },
            }}
          >
            <QuillR2Editor
              value={sq.question_text || ""}
              onChange={(content) => onChange({ question_text: content })}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Enter sub-question text (formatting, lists, images…)"
              showMathTools
            />
          </Box>
          {subStemPreview && (
            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: "grey.50",
                maxHeight: 220,
                overflow: "auto",
                "& mjx-container": { fontSize: "0.9rem" },
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                Preview (MathJax)
              </Typography>
              {stripToPlainText(sq.question_text || "").trim() ? (
                <HtmlWithLatex html={stripTagsKeepLatex(sq.question_text || "")} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Type text to see preview.
                </Typography>
              )}
            </Paper>
          )}
        </Grid>

        {/* Image upload */}
        <Grid item xs={12}>
          <input
            type="file"
            accept="image/png, image/jpeg, image/webp"
            onChange={handleImageUpload}
            style={{ display: "none" }}
            ref={fileRef}
            disabled={isImageProcessing}
          />
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#444", display: "block", mb: 0.75 }}>
            Image (optional)
          </Typography>
          {sq.image_url && (
            <Box sx={{ display: "inline-flex", position: "relative", mb: 1 }}>
              <img
                src={sq.image_url}
                alt="sub-question"
                style={{ maxWidth: 160, maxHeight: 120, borderRadius: 4, border: "1px solid #e0e0e0", objectFit: "contain" }}
                onError={(e) => { e.target.style.opacity = "0.3"; }}
              />
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                disabled={isImageProcessing}
                sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(255,255,255,0.85)", color: "#f44336", p: 0.25 }}
              >
                <Delete sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
          <Box
            onDragOver={(e) => {
              if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
            }}
            onDrop={async (e) => {
              const file = e.dataTransfer?.files?.[0];
              if (!file) return;
              e.preventDefault();
              await uploadImageFile(file);
            }}
            onClick={() => !isImageProcessing && fileRef.current?.click()}
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: "grey.50",
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              cursor: isImageProcessing ? "not-allowed" : "pointer",
              "&:hover": { borderColor: "#6366f1", bgcolor: "rgba(99,102,241,0.03)" },
            }}
          >
            {isImageProcessing ? (
              <CircularProgress size={20} />
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.82rem" }}>
                {sq.image_url ? "Drop to replace, or click to browse" : "Drop image here, or click to browse"}
              </Typography>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Image description (optional)"
            value={sq.image_description || ""}
            onChange={(e) => onChange({ image_description: e.target.value })}
            placeholder="Accessibility / diagram generation hint"
            sx={{ mt: 1 }}
            InputProps={{
              endAdornment: sq.image_description ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onChange({ image_description: "" })} title="Delete description">
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>

        {/* Answer config */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#444" }}>
              Answer Configuration
            </Typography>
            {canUseSubAi && (
              <Button
                size="small"
                variant="outlined"
                startIcon={generatingAnswer ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                onClick={handleSubGenerateAnswer}
                disabled={generatingAnswer || !sq.id}
                sx={{ py: 0.25, px: 1, fontSize: "0.75rem", textTransform: "none" }}
              >
                {generatingAnswer ? "Generating…" : "AI Generate Answer"}
              </Button>
            )}
          </Box>
          {renderAnswerEditor()}
        </Grid>

        {/* Sub-question explanation */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#444" }}>
              Explanation (Optional)
            </Typography>
            {canUseSubAi && (
              <Button
                size="small"
                variant="outlined"
                startIcon={generatingExplanation ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                onClick={handleSubGenerateExplanation}
                disabled={generatingExplanation || improving}
                sx={{ py: 0.25, px: 1, fontSize: "0.75rem", textTransform: "none" }}
              >
                {generatingExplanation ? "Generating…" : "AI Generate"}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              "& .ql-toolbar": { borderRadius: "6px 6px 0 0" },
              "& .ql-container": { minHeight: 120, borderRadius: "0 0 6px 6px", fontSize: "0.9rem" },
            }}
          >
            <QuillR2Editor
              value={sq.explanation || ""}
              onChange={(html) => onChange({ explanation: normalizeQuillValue(html) })}
              modules={EXPL_QUILL_MODULES}
              formats={EXPL_QUILL_FORMATS}
              placeholder="Explain the correct answer for student review..."
              style={{ minHeight: 140 }}
              showMathTools
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 7 }}>
            Shown to students after they complete the exam.
          </Typography>
        </Grid>

      </Grid>
    </Paper>

      <Dialog open={subAiImproveOpen} onClose={() => setSubAiImproveOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Sub-question AI suggestion — compare and accept</DialogTitle>
        <DialogContent dividers>
          <AiAcceptSections value={subAiAccept} onChange={setSubAiAccept} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                Original
              </Typography>
              {subAiOriginal && (
                <>
                  <QuestionPaperLayout
                    question={subAiOriginal}
                    questionNumber={sqIndex + 1}
                    showAnswers
                    showAdminHints={false}
                  />
                  <AiExplanationPreview html={subAiOriginal.explanation} />
                </>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, border: "2px solid", borderColor: "secondary.main" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "secondary.main" }}>
                AI suggestion — preview of what will be applied
              </Typography>
              {subAiSuggestion && (() => {
                const merged = mergeAiSuggestion(subAiOriginal, subAiSuggestion, subAiAccept);
                return (
                  <>
                    <QuestionPaperLayout
                      question={merged}
                      questionNumber={sqIndex + 1}
                      showAnswers
                      showAdminHints={false}
                    />
                    <AiExplanationPreview html={merged.explanation} />
                  </>
                );
              })()}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubAiImproveOpen(false)}>Dismiss</Button>
          <Button
            variant="contained"
            onClick={() => {
              const patch = applyImprovedSubQuestionPatch(
                mergeAiSuggestion(subAiOriginal, subAiSuggestion, subAiAccept)
              );
              if (Object.keys(patch).length > 0) onChange(patch);
              setSubAiImproveOpen(false);
              toast.success("Applied AI suggestion to this sub-question. Review and save when ready.");
            }}
            disabled={!subAiSuggestion || !Object.values(subAiAccept).some(Boolean)}
            sx={{ textTransform: "none" }}
          >
            Accept and apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const QuestionBankForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state: locationState } = useLocation();
  const isEdit = !!id;
  const returnTo = locationState?.returnTo || "/admin/question-bank";
  const fileInputRef = useRef(null);
  const passageFormQuillRef = useRef(null);
  const savedPassageFormSelectionRef = useRef(null);
  const [formManualLineNum, setFormManualLineNum] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const [isGeneratingDiagram, setIsGeneratingDiagram] = useState(false);
  const [isGeneratingAnswer, setIsGeneratingAnswer] = useState(false);
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [improving, setImproving] = useState(false);
  const [aiImproveOpen, setAiImproveOpen] = useState(false);
  const [aiOriginal, setAiOriginal] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiAccept, setAiAccept] = useState(AI_ACCEPT_DEFAULT);
  const [questionLivePreview, setQuestionLivePreview] = useState(true);
  const [imageDropActive, setImageDropActive] = useState(false);
  const [markdownDialogOpen, setMarkdownDialogOpen] = useState(false);

  // Form fields
  const [questionText, setQuestionText] = useState("");
  const [questionTextError, setQuestionTextError] = useState("");
  const [type, setType] = useState("multiple-choice");
  const [categoryId, setCategoryId] = useState("");
  const [difficulty, setDifficulty] = useState("Medium");
  const [defaultMarks, setDefaultMarks] = useState(1);
  const [isSampleQuestion, setIsSampleQuestion] = useState(false);
  const [mcqRecommended, setMcqRecommended] = useState(false);
  const [mcqRecommendedNote, setMcqRecommendedNote] = useState("");
  const [defaultDurationSec, setDefaultDurationSec] = useState("");
  const [topic, setTopic] = useState("");
  const [subjectId, setSubjectId] = useState(null);
  // Kept so a second click can correct the figure instead of redrawing from a blank slate.
  const [aiVisualSpec, setAiVisualSpec] = useState(null);
  const [topicIds, setTopicIds] = useState([]);
  const [topicCatalog, setTopicCatalog] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [topicYearId, setTopicYearId] = useState("");
  const [passageType, setPassageType] = useState("");
  const [imageUrls, setImageUrls] = useState([]); // all images; [0] is primary
  const [imageDescription, setImageDescription] = useState("");
  const [blankLabels, setBlankLabels] = useState([]);
  const [blankMarks, setBlankMarks] = useState([]);
  const [blankLabelPositions, setBlankLabelPositions] = useState([]);
  const [fillMarksMode, setFillMarksMode] = useState("sum");
  const [fillFixedTotal, setFillFixedTotal] = useState(4);
  const [tableLayout, setTableLayout] = useState(() => DEFAULT_GRID());
  const [overlayLayout, setOverlayLayout] = useState(() => ({ image_url: "", blanks: [] }));
  const [diagramType, setDiagramType] = useState("");
  const [hubValue, setHubValue] = useState("");
  const [semanticMatch, setSemanticMatch] = useState(false);
  // "Must be written exactly like the mark scheme" — every fuzzy tier and the AI grader off for this
  // answer. For mark schemes where the notation IS the answer (an expression, a formula, a given form).
  const [exactMatch, setExactMatch] = useState(false);

  // Type-specific state
  const [options, setOptions] = useState([
    { text: "", image_url: "" },
    { text: "", image_url: "" },
  ]);
  const [correctOptionIndex, setCorrectOptionIndex] = useState(0);
  const [correctOptionIndices, setCorrectOptionIndices] = useState([]);
  const [trueFalseAnswer, setTrueFalseAnswer] = useState(true);
  const [blanks, setBlanks] = useState([[""]]);
  const [modelAnswer, setModelAnswer] = useState("");
  const [shortAnswer, setShortAnswer] = useState("");
  const [correctSolutionImageUrl, setCorrectSolutionImageUrl] = useState("");
  const [generatingAnswerImage, setGeneratingAnswerImage] = useState(false);
  const [interactiveSpec, setInteractiveSpec] = useState(null);
  const [forceMeasurementTool, setForceMeasurementTool] = useState("auto");
  const [explanation, setExplanation] = useState("");
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionInstructions, setSectionInstructions] = useState("");

  // Video explanation state
  const [videoData, setVideoData] = useState(null);

  const IN_PROGRESS_VIDEO_STATUSES = ['pending', 'parsing', 'rendering', 'composing', 'uploading'];

  // Import linkage + extracted images panel state
  const [importId, setImportId] = useState(null);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [extractedImages, setExtractedImages] = useState([]);
  const [imagesPanelQuestions, setImagesPanelQuestions] = useState([]);
  const qLabelMap = useMemo(() => {
    const map = {};
    let parentNum = 0;
    const parentNums = {};
    imagesPanelQuestions.forEach((q) => {
      if (!q.parent_question_id) {
        parentNum++;
        parentNums[String(q.id)] = parentNum;
        map[String(q.id)] = `Q${parentNum}`;
      }
    });
    const subCounters = {};
    imagesPanelQuestions.forEach((q) => {
      if (q.parent_question_id) {
        const pk = String(q.parent_question_id);
        subCounters[pk] = (subCounters[pk] || 0) + 1;
        map[String(q.id)] = `Q${parentNums[pk] ?? '?'}.${subCounters[pk]}`;
      }
    });
    return map;
  }, [imagesPanelQuestions]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [reassigningImage, setReassigningImage] = useState(null);
  const [removingExtractedImage, setRemovingExtractedImage] = useState(null);
  const [dragImageIndex, setDragImageIndex] = useState(null);
  const [dragOverImageIndex, setDragOverImageIndex] = useState(null);
  const draggedExtractedImageRef = useRef(null);
  const [isDropOverImageArea, setIsDropOverImageArea] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignTargetImage, setReassignTargetImage] = useState(null);
  const [reassignSelectedQuestion, setReassignSelectedQuestion] = useState("");
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [applyChangesResult, setApplyChangesResult] = useState(null);

  // Sub-questions (for grouped type)
  const [subQuestions, setSubQuestions] = useState([]);
  const [subLabelType, setSubLabelType] = useState("letter");
  const [groupedMarksMode, setGroupedMarksMode] = useState("sum"); // 'sum' | 'fixed'
  const [groupedFixedTotal, setGroupedFixedTotal] = useState(4);
  const [groupedExtend, setGroupedExtend] = useState(false); // show all parts expanded (plotting/drawing groups)

  const subDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleSubDragEnd = async ({ active, over }) => {
    if (!active?.id || !over?.id || String(active.id) === String(over.id)) return;
    const keys = subQuestions.map((sq) => sq._key);
    const oldIdx = keys.indexOf(String(active.id));
    const newIdx = keys.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    const newSubs = arrayMove(subQuestions, oldIdx, newIdx);
    setSubQuestions(newSubs);
    if (isEdit && id) {
      const existingIds = newSubs.map((sq) => sq.id).filter(Boolean);
      if (existingIds.length > 0) {
        api.patch(`/admin/question-bank/${id}/reorder-sub-questions`, { child_ids: existingIds }).catch(() => {});
      }
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    api.get("/admin/master-form/school-years")
      .then((res) => {
        const rows = res.data?.data || [];
        setSchoolYears(rows);
        const y5 = rows.find((y) => String(y.name || "").toLowerCase().replace(/\s+/g, "") === "year5");
        if (y5) setTopicYearId(String(y5.id));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) return;
    api.get("/admin/mock-exam/category-tree")
      .then((res) => {
        const tree = res.data?.data || [];
        const id = findCategoryIdByName(tree, "Past Papers");
        if (id) setCategoryId(id);
      })
      .catch(() => {});
  }, [isEdit]);

  useEffect(() => {
    if (!subjectId) {
      setTopicCatalog([]);
      return;
    }
    let cancelled = false;
    api.get("/admin/question-bank/topics-by-subject", { params: { subject_id: subjectId, include_counts: 1 } })
      .then((res) => {
        if (cancelled) return;
        setTopicCatalog(res.data?.data || []);
      })
      .catch(() => { if (!cancelled) setTopicCatalog([]); });
    return () => { cancelled = true; };
  }, [subjectId]);

  // Poll video status while a generation job is running
  useEffect(() => {
    if (!isEdit || !videoData?.video_generation_status) return;
    if (!IN_PROGRESS_VIDEO_STATUSES.includes(videoData.video_generation_status)) return;
    const pollTimer = setInterval(async () => {
      try {
        const res = await api.get(`/admin/question-bank/${id}`);
        const q = res.data?.data;
        if (q) {
          setVideoData({
            has_linked_course_question: true,
            explanation_video_url: q.explanation_video_url,
            video_generation_status: q.video_generation_status,
          });
        }
      } catch {
        // silently ignore — panel keeps showing last known status
      }
    }, 5000);
    return () => clearInterval(pollTimer);
  }, [isEdit, id, videoData?.video_generation_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQuestion = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/${id}`);
      const q = res.data?.data;
      if (!q) return;

      setQuestionText(q.question_text);
      setType(q.type);
      setCategoryId(q.category_id || "");
      setDifficulty(q.difficulty);
      setDefaultMarks(q.default_marks);
      setIsSampleQuestion(!!q.is_sample_question);
      setMcqRecommended(!!q.mcq_recommended);
      setMcqRecommendedNote(q.mcq_recommended_note || "");
      setDefaultDurationSec(q.default_duration_sec || "");
      setTopic(q.topic || "");
      const derivedSubjectId = q.subject_id ||
        (q.type === 'grouped' && Array.isArray(q.sub_questions)
          ? (q.sub_questions.find(sq => sq.subject_id)?.subject_id ?? null)
          : null);
      setSubjectId(derivedSubjectId);
      setTopicIds(Array.isArray(q.topic_ids) ? q.topic_ids.map((n) => Number(n)) : []);
      setPassageType(q.passage_type || "");
      const imgs =
        Array.isArray(q.image_urls) && q.image_urls.filter(Boolean).length > 0
          ? q.image_urls.filter(Boolean)
          : q.image_url
            ? [q.image_url]
            : [];
      setImageUrls(imgs);
      setImageDescription(q.image_description || "");
      setBlankLabels(q.answer?.blank_labels || []);
      setBlankMarks(q.answer?.blank_marks || []);
      setDiagramType(q.answer?.diagram_type || "");
      setHubValue(q.answer?.hub_value || "");
      setImportId(q.import_id || null);
      setExplanation(q.explanation || "");
      setSectionTitle(q.section_title || "");
      setSectionInstructions(q.section_instructions || "");

      // Set video data if available
      if (q.explanation_video_url || q.video_generation_status) {
        setVideoData({
          has_linked_course_question: true,
          explanation_video_url: q.explanation_video_url,
          video_generation_status: q.video_generation_status,
        });
      }

      // Populate type-specific fields
      populateTypeFields(q);

      if (["fill-in-the-blank", "multi-fill", "table"].includes(q.type)) {
        const ad = q.answer_data || {};
        if (ad.marks_mode === "fixed") {
          setFillMarksMode("fixed");
          setFillFixedTotal(ad.marks_total ?? q.default_marks ?? 4);
        } else {
          setFillMarksMode("sum");
        }
        const positions = ad.blank_label_positions ?? q.answer?.blank_label_positions;
        if (Array.isArray(positions) && positions.length > 0) {
          setBlankLabelPositions(positions);
        }
      }

      // Populate sub-questions for grouped questions
      if (q.type === "grouped" && Array.isArray(q.sub_questions)) {
        setSubQuestions(q.sub_questions.map(buildSubQuestionState));
        setSubLabelType(q.sub_label_type || "letter");
        const ad = q.answer_data || {};
        if (ad.marks_mode === "fixed") {
          setGroupedMarksMode("fixed");
          setGroupedFixedTotal(ad.marks_total ?? q.default_marks ?? 1);
        } else {
          setGroupedMarksMode("sum");
        }
        setGroupedExtend(!!ad.extend_parts);
      }
    } catch (err) {
      toast.error("Failed to load question");
      navigate(returnTo);
    } finally {
      setLoading(false);
    }
  };

  const populateTypeFields = (q) => {
    const opts = q.options || [];
    const ans = q.answer || {};
    setSemanticMatch(!!ans.semantic_match);
    setExactMatch(!!ans.exact_match);
    setForceMeasurementTool(
      (ans.solution_geometry && ans.solution_geometry.force_measurement_tool)
      || (ans.solution_geometry?.interactive?.force_tool)
      || "auto"
    );

    switch (q.type) {
      case "multiple-choice":
        setOptions(
          opts.map((o) => ({ text: o.option_text, image_url: o.image_url || "" }))
        );
        const correctIdx = opts.findIndex((o) => o.is_correct);
        setCorrectOptionIndex(correctIdx >= 0 ? correctIdx : 0);
        break;
      case "multiple-select":
        setOptions(
          opts.map((o) => ({ text: o.option_text, image_url: o.image_url || "" }))
        );
        setCorrectOptionIndices(
          opts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), [])
        );
        break;
      case "true-false":
        const trueOpt = opts.find((o) => o.option_text === "True");
        setTrueFalseAnswer(trueOpt?.is_correct ?? true);
        break;
      case "fill-in-the-blank":
      case "multi-fill":
        setBlanks(normalizeBlanks(ans.correct_blanks) || [[""]]);
        if (ans.blank_labels?.length > 0) setBlankLabels(ans.blank_labels);
        if (ans.blank_marks?.length > 0) setBlankMarks(ans.blank_marks);
        if (ans.blank_label_positions?.length > 0) setBlankLabelPositions(ans.blank_label_positions);
        setOverlayLayout((ans.overlay_layout?.image_url || ans.overlay_layout?.mode || ans.overlay_layout?.rows_interchangeable || ans.overlay_layout?.blanks_interchangeable) ? ans.overlay_layout : { image_url: "", blanks: [] });
        break;
      case "table":
        setBlanks(normalizeBlanks(ans.correct_blanks) || Array.from({ length: 9 }, () => [""]));
        if (ans.blank_labels?.length > 0) setBlankLabels(ans.blank_labels);
        if (ans.blank_marks?.length > 0) setBlankMarks(ans.blank_marks);
        setTableLayout(ans.table_layout?.grid ? ans.table_layout : DEFAULT_GRID());
        if (ans.overlay_layout?.rows_interchangeable) setOverlayLayout(ans.overlay_layout);
        break;
      case "image-fill":
        setBlanks(normalizeBlanks(ans.correct_blanks) || [[""]]);
        if (ans.blank_labels?.length > 0) setBlankLabels(ans.blank_labels);
        if (ans.blank_marks?.length > 0) setBlankMarks(ans.blank_marks);
        setOverlayLayout((ans.overlay_layout?.image_url || ans.overlay_layout?.mode || ans.overlay_layout?.rows_interchangeable || ans.overlay_layout?.blanks_interchangeable) ? ans.overlay_layout : { image_url: "", blanks: [] });
        break;
      case "matching":
        setOptions(
          opts.map((o) => ({
            text: o.option_text,
            match_text: o.match_text || "",
            image_url: o.image_url || "",
          }))
        );
        break;
      case "ordering":
        setOptions(
          opts.map((o) => ({ text: o.option_text, image_url: o.image_url || "" }))
        );
        break;
      case "essay":
        setModelAnswer(ans.correct_answer_text || "");
        break;
      case "short-answer":
        setShortAnswer(ans.correct_answer_text || "");
        break;
      case "drawing":
        setShortAnswer(ans.correct_answer_text || "");
        setCorrectSolutionImageUrl(ans.correct_solution_image_url || "");
        setInteractiveSpec((ans.solution_geometry && ans.solution_geometry.interactive) || null);
        break;
      default:
        break;
    }
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    // Reset type-specific fields
    setOptions([
      { text: "", image_url: "" },
      { text: "", image_url: "" },
    ]);
    setCorrectOptionIndex(0);
    setCorrectOptionIndices([]);
    setTrueFalseAnswer(true);
    setBlanks([[""]]);
    setBlankLabels([]);
    setBlankMarks([]);
    setBlankLabelPositions([]);
    setFillMarksMode("sum");
    setFillFixedTotal(4);
    setTableLayout(DEFAULT_GRID());
    setOverlayLayout({ image_url: "", blanks: [] });
    setModelAnswer("");
    setShortAnswer("");
    setCorrectSolutionImageUrl("");
    setInteractiveSpec(null);
    setSubQuestions([]);
    setSubLabelType("letter");
    setGroupedMarksMode("sum");
    setGroupedFixedTotal(4);
    setGroupedExtend(false);

    if (newType === "multi-fill") {
      setBlanks([[""], [""]]);
    }
    if (newType === "table") {
      setBlanks(Array.from({ length: 9 }, () => [""]));
      setBlankMarks(Array.from({ length: 9 }, () => 1));
      setTableLayout(DEFAULT_GRID());
    }
    if (newType === "grouped") {
      setSubQuestions([defaultSubQuestion()]);
    }
  };

  const handleFormInsertLineNumbers = () => {
    const numbered = insertLineNumberMarkers(questionText || "");
    setQuestionText(numbered);
    toast.success("Line numbers inserted. Edit the {L…} markers if needed.");
  };

  const handleFormClearLineNumbers = () => {
    setQuestionText(stripLineNumberMarkers(questionText || ""));
  };

  const handleFormInsertManualLineNum = () => {
    const num = parseInt(formManualLineNum, 10);
    if (!num || num < 1) return;
    const marker = `{L${num}} `;
    const editor = passageFormQuillRef.current?.getEditor?.();
    if (editor) {
      const saved = savedPassageFormSelectionRef.current;
      const insertAt = saved ? saved.index : editor.getLength() - 1;
      editor.insertText(insertAt, marker, "user");
      editor.setSelection(insertAt + marker.length);
      editor.focus();
    } else {
      setQuestionText((questionText || "") + marker);
    }
    setFormManualLineNum("");
  };

  const uploadStemImageFile = async (file) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPEG, or WebP).");
      return;
    }
    setIsImageProcessing(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await api.post("/admin/question-bank/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newUrl = res.data?.data?.image_url || "";
      if (newUrl) {
        const newUrls = [...imageUrls, newUrl];
        setImageUrls(newUrls);
        if (isEdit && id) {
          await api.patch(`/admin/question-bank/${id}`, { image_urls: newUrls });
          toast.success("Image attached and saved");
        } else {
          toast.success("Image uploaded successfully");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not upload image");
    } finally {
      setIsImageProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadStemImageFile(file);
  };

  const buildAiQuestionPayload = () => {
    const base = {
      type,
      question_text: questionText,
      difficulty,
      default_marks: parseInt(defaultMarks, 10) || 1,
      topic: topic || null,
      image_description: imageDescription || null,
      image_url: imageUrls[0] || null,
      image_urls: imageUrls.length > 0 ? imageUrls : null,
      section_title: sectionTitle || null,
      section_instructions: sectionInstructions || null,
      explanation: explanation || null,
    };
    switch (type) {
      case "multiple-choice": {
        const opts = options.map((o, i) => ({
          text: o.text || "",
          option_text: o.text || "",
          image_url: o.image_url || null,
          is_correct: i === correctOptionIndex,
        }));
        return {
          ...base,
          options: opts,
          answer_data: { correct_options: [correctOptionIndex] },
        };
      }
      case "multiple-select": {
        const opts = options.map((o, i) => ({
          text: o.text || "",
          option_text: o.text || "",
          image_url: o.image_url || null,
          is_correct: correctOptionIndices.includes(i),
        }));
        return {
          ...base,
          options: opts,
          answer_data: { correct_options: [...correctOptionIndices] },
        };
      }
      case "true-false": {
        const opts = [
          { text: "True", option_text: "True", is_correct: trueFalseAnswer },
          { text: "False", option_text: "False", is_correct: !trueFalseAnswer },
        ];
        return {
          ...base,
          options: opts,
          answer_data: { correct_options: [trueFalseAnswer ? 0 : 1] },
        };
      }
      case "fill-in-the-blank":
      case "multi-fill":
        return {
          ...base,
          answer_data: {
            correct_blanks: blanks,
            ...(blankLabels.length > 0 ? { blank_labels: blankLabels } : {}),
          },
        };
      case "table":
        return {
          ...base,
          type: "table",
          answer_data: {
            correct_blanks: blanks,
            ...(blankLabels.length > 0 ? { blank_labels: blankLabels } : {}),
            ...(blankMarks.length > 0 ? { blank_marks: blankMarks } : {}),
            ...(tableLayout?.grid?.length > 0 ? { table_layout: tableLayout } : {}),
            ...(overlayLayout?.rows_interchangeable ? { overlay_layout: { rows_interchangeable: true, ...(overlayLayout?.interchangeable_row_indices?.length ? { interchangeable_row_indices: overlayLayout.interchangeable_row_indices } : {}) } } : {}),
          },
        };
      case "image-fill":
        return {
          ...base,
          type: "image-fill",
          answer_data: {
            correct_blanks: blanks,
            ...(blankLabels.length > 0 ? { blank_labels: blankLabels } : {}),
            ...(blankMarks.length > 0 ? { blank_marks: blankMarks } : {}),
            ...((overlayLayout?.image_url || overlayLayout?.mode === "inline" || overlayLayout?.blanks_interchangeable) ? { overlay_layout: overlayLayout } : {}),
          },
        };
      case "short-answer":
        return {
          ...base,
          answer_data: { correct_answer_text: shortAnswer },
        };
      case "essay":
        return {
          ...base,
          answer_data: { correct_answer_text: modelAnswer, model_answer: modelAnswer },
        };
      case "matching":
        return {
          ...base,
          options: options.map((o) => ({
            text: o.text || "",
            option_text: o.text || "",
            image_url: o.image_url || null,
            match_text: o.match_text || "",
            is_correct: true,
          })),
          answer_data: {},
        };
      case "ordering":
        return {
          ...base,
          options: options.map((o) => ({
            text: o.text || "",
            option_text: o.text || "",
            image_url: o.image_url || null,
            is_correct: true,
          })),
          answer_data: {},
        };
      default:
        return base;
    }
  };

  const applyImprovedQuestionToForm = (imp) => {
    if (!imp || typeof imp !== "object") return;
    if (imp.question_text != null) setQuestionText(imp.question_text);
    if (imp.difficulty) setDifficulty(imp.difficulty);
    if (imp.default_marks != null) setDefaultMarks(imp.default_marks);
    if (imp.is_sample_question !== undefined) setIsSampleQuestion(!!imp.is_sample_question);
    if (imp.topic !== undefined) setTopic(imp.topic || "");
    if (imp.image_description != null) setImageDescription(imp.image_description || "");
    if (imp.explanation != null) setExplanation(imp.explanation || "");
    if (imp.section_title !== undefined) setSectionTitle(imp.section_title || "");
    if (imp.section_instructions !== undefined) setSectionInstructions(imp.section_instructions || "");
    const urls =
      Array.isArray(imp.image_urls) && imp.image_urls.filter(Boolean).length > 0
        ? imp.image_urls.filter(Boolean)
        : imp.image_url
          ? [imp.image_url]
          : null;
    if (urls && urls.length > 0) setImageUrls(urls);

    const ad = imp.answer_data || {};
    const opts = imp.options || [];
    const impType = imp.type || type;

    if (impType === "multiple-choice" && opts.length > 0) {
      setOptions(
        opts.map((o) => ({
          text: o.text || o.option_text || "",
          image_url: o.image_url || "",
        }))
      );
      const ci = opts.findIndex((o) => o.is_correct);
      if (ci >= 0) setCorrectOptionIndex(ci);
      else if (Array.isArray(ad.correct_options) && ad.correct_options.length > 0) {
        setCorrectOptionIndex(ad.correct_options[0]);
      }
    }
    if (impType === "multiple-select" && opts.length > 0) {
      setOptions(
        opts.map((o) => ({
          text: o.text || o.option_text || "",
          image_url: o.image_url || "",
        }))
      );
      const idxs = opts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), []);
      if (idxs.length > 0) setCorrectOptionIndices(idxs);
      else if (Array.isArray(ad.correct_options)) setCorrectOptionIndices(ad.correct_options);
    }
    if (impType === "true-false") {
      const trueOpt = opts.find((o) => (o.text || o.option_text) === "True");
      if (trueOpt && trueOpt.is_correct !== undefined) setTrueFalseAnswer(!!trueOpt.is_correct);
    }
    if (impType === "fill-in-the-blank" || impType === "multi-fill") {
      if (ad.correct_blanks?.length > 0) setBlanks(normalizeBlanks(ad.correct_blanks) || [[""]]);
      if (ad.blank_labels?.length > 0) setBlankLabels(ad.blank_labels);
      if (ad.blank_marks?.length > 0) setBlankMarks(ad.blank_marks);
    }
    if (impType === "short-answer" && ad.correct_answer_text) setShortAnswer(ad.correct_answer_text);
    if (impType === "essay" && ad.correct_answer_text) setModelAnswer(ad.correct_answer_text);
    if (impType === "matching" && opts.length > 0) {
      setOptions(
        opts.map((o) => ({
          text: o.text || o.option_text || "",
          match_text: o.match_text || "",
          image_url: o.image_url || "",
        }))
      );
    }
    if (impType === "ordering" && opts.length > 0) {
      setOptions(
        opts.map((o) => ({
          text: o.text || o.option_text || "",
          image_url: o.image_url || "",
        }))
      );
    }
  };

  const handleGenerateExplanationAi = async () => {
    if (!isEdit || !id || type === "grouped") return;
    setGeneratingExplanation(true);
    try {
      const res = await api.post(`/admin/question-bank/${id}/generate-explanation`, {
        question: buildAiQuestionPayload(),
      });
      const text = res.data?.data?.explanation;
      if (text) {
        const prev = explanation || "";
        if (/<img\s/i.test(prev) && prev.replace(/<[^>]*>/g, "").trim()) {
          setExplanation(`${prev}<p><br></p>${formatExplanationStepwise(text)}`);
        } else {
          setExplanation(formatExplanationStepwise(text));
        }
      }
      toast.success("Explanation generated in step-wise format");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate explanation");
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const handleAiImprove = async () => {
    if (!isEdit || !id || type === "grouped") return;
    setImproving(true);
    try {
      const payload = buildAiQuestionPayload();
      const res = await api.post(`/admin/question-bank/${id}/ai-improve-question`, {
        question: payload,
      });
      const imp = res.data?.data?.improved_question;
      setAiOriginal(payload);
      setAiSuggestion(withFormattedExplanation(imp));
      setAiAccept(AI_ACCEPT_DEFAULT);
      setAiImproveOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "AI improvement failed. Please try again.");
    } finally {
      setImproving(false);
    }
  };

  const handleRemoveImage = async (urlToRemove) => {
    const target = urlToRemove || imageUrls[0];
    if (!target) return;

    setIsImageProcessing(true);
    const newUrls = imageUrls.filter((u) => u !== target);
    setImageUrls(newUrls);

    try {
      await api.post("/admin/question-bank/delete-image", { url: target });
      // In edit mode, save only image fields (partial update), not the full question
      if (isEdit && id) {
        await api.patch(`/admin/question-bank/${id}`, { image_urls: newUrls.length > 0 ? newUrls : [] });
        toast.success("Image removed and saved");
      } else {
        toast.success("Image removed");
      }
    } catch (error) {
      setImageUrls((prev) => [...prev, target]);
      toast.error(error.response?.data?.message || "Could not delete the image");
    } finally {
      setIsImageProcessing(false);
    }
  };

  // ── Blank-label helpers (hub-spoke spoke expressions) ────────────────────
  const addBlankLabel = () => setBlankLabels((prev) => [...prev, ""]);
  const removeBlankLabel = (idx) => setBlankLabels((prev) => prev.filter((_, i) => i !== idx));
  const updateBlankLabel = (idx, value) => {
    setBlankLabels((prev) => prev.map((l, i) => (i === idx ? value : l)));
    // Auto-populate hub value from the "= N" suffix on the first label when not yet set
    if (idx === 0) {
      const m = value.match(/=\s*(\S+)\s*$/);
      if (m && !hubValue) setHubValue(m[1]);
    }
  };

  const handleGenerateDiagram = async () => {
    if (!isEdit) return;
    setIsGeneratingDiagram(true);
    try {
      const body = {
        image_description: imageDescription || "",
      };
      // Always send blank_labels so the backend can use the correct expressions
      // even when image_description is empty or the DB column is not yet backfilled
      if (blankLabels.length > 0) {
        body.blank_labels = blankLabels;
      }
      // Send explicit hub_value so backend can use native generator even when
      // blank_labels don't carry the "= N" suffix themselves
      if (hubValue) {
        body.hub_value = hubValue;
      }
      const res = await api.post(`/admin/question-bank/${id}/generate-diagram`, body);
      const resData = res.data?.data || {};
      const newUrl = resData.image_url || "";
      if (newUrl) setImageUrls((prev) => {
        const filtered = prev.filter((u) => u !== newUrl);
        return [newUrl, ...filtered];
      });
      // If the backend inferred a description (auto-generated from correct_blanks), populate the field
      if (resData.image_description && !imageDescription) {
        setImageDescription(resData.image_description);
      }
      // Store diagram_type / hub_value returned by native generator
      if (resData.diagram_type) {
        setDiagramType(resData.diagram_type);
        setHubValue(resData.hub_value || "");
      }
      toast.success("Diagram generated and saved. All linked mock exams will show the new image.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Diagram generation failed. Please try again.");
    } finally {
      setIsGeneratingDiagram(false);
    }
  };

  const handleGenerateAnswerImage = async () => {
    if (!isEdit || !id) {
      toast.info("Save the question first, then generate the answer diagram.");
      return;
    }
    setGeneratingAnswerImage(true);
    try {
      const res = await api.post(`/admin/question-bank/${id}/generate-answer-image`, {
        image_url: imageUrls[0] || null,
      });
      const url = res.data?.data?.correct_solution_image_url || "";
      if (url) {
        setCorrectSolutionImageUrl(url);
        toast.success("Answer diagram generated and saved. Review before saving the question.");
      } else {
        toast.error("No answer image was returned.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate answer diagram.");
    } finally {
      setGeneratingAnswerImage(false);
    }
  };

  const handleGenerateAnswer = async () => {
    if (!isEdit) return;
    setIsGeneratingAnswer(true);
    try {
      const res = await api.post(`/admin/question-bank/${id}/generate-answer`, {
        question: buildAiQuestionPayload(),
      });
      const answerData = res.data?.data?.answer_data || {};
      const hasImage = res.data?.data?.has_image;

      switch (type) {
        case "multiple-choice":
          if (answerData.correct_options?.length > 0) {
            setCorrectOptionIndex(answerData.correct_options[0]);
          }
          break;
        case "multiple-select":
          if (answerData.correct_options?.length > 0) {
            setCorrectOptionIndices(answerData.correct_options);
          }
          break;
        case "true-false":
          if (answerData.correct_options !== undefined) {
            // correct_options[0] === 0 → True is correct (True is at index 0)
            setTrueFalseAnswer((answerData.correct_options?.[0] ?? 0) === 0);
          }
          break;
        case "fill-in-the-blank":
        case "multi-fill":
          if (answerData.correct_blanks?.length > 0) {
            setBlanks(normalizeBlanks(answerData.correct_blanks) || [[""]]);
          }
          if (answerData.blank_labels?.length > 0) setBlankLabels(answerData.blank_labels);
          if (answerData.blank_marks?.length > 0) setBlankMarks(answerData.blank_marks);
          break;
        case "short-answer":
          if (answerData.correct_answer_text) {
            setShortAnswer(answerData.correct_answer_text);
          }
          break;
        case "essay":
          if (answerData.correct_answer_text) {
            setModelAnswer(answerData.correct_answer_text);
          }
          break;
        case "matching":
          if (answerData.correct_matches?.length > 0) {
            setOptions((prev) =>
              prev.map((opt) => {
                const match = answerData.correct_matches.find(
                  (m) =>
                    m.left &&
                    opt.text &&
                    m.left.trim().toLowerCase() === opt.text.trim().toLowerCase()
                );
                return match ? { ...opt, match_text: match.right || "" } : opt;
              })
            );
          }
          break;
        case "ordering":
          if (answerData.correct_ordering_items?.length > 0) {
            const orderedTexts = answerData.correct_ordering_items;
            setOptions((prev) => {
              const mapped = orderedTexts.map((text) => {
                const found = prev.find(
                  (o) => o.text.trim().toLowerCase() === text.trim().toLowerCase()
                );
                return found || { text };
              });
              return mapped;
            });
          }
          break;
        default:
          break;
      }

      const provider = hasImage ? "GPT-4o (vision)" : "AI";
      toast.success(`Answer generated using ${provider}. Review and save if correct.`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to generate answer. Please try again."
      );
    } finally {
      setIsGeneratingAnswer(false);
    }
  };

  // ── Extracted Images panel handlers ──────────────────────────────────────
  const fetchExtractedImages = async () => {
    if (!importId) return;
    setImagesLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/import/${importId}/images`);
      const d = res.data?.data;
      setExtractedImages(d?.images || []);
      setImagesPanelQuestions(d?.questions || []);
      setImagesLoaded(true);
    } catch {
      toast.error("Failed to load extracted images");
    } finally {
      setImagesLoading(false);
    }
  };

  const toggleImagesPanel = async () => {
    const willOpen = !imagesOpen;
    setImagesOpen(willOpen);
    if (willOpen && !imagesLoaded) await fetchExtractedImages();
  };

  const openReassignDialog = (img) => {
    setReassignTargetImage(img);
    setReassignSelectedQuestion(img.assigned_question_id != null ? String(img.assigned_question_id) : "");
    setReassignDialogOpen(true);
  };

  const handleReassignConfirm = async () => {
    if (!reassignTargetImage) return;

    // Capture values immediately — avoids stale-closure issues after awaits
    const targetImage = reassignTargetImage;
    const newQId = reassignSelectedQuestion === "" ? null : reassignSelectedQuestion;
    const prevQId = targetImage.assigned_question_id != null ? String(targetImage.assigned_question_id) : null;
    const currentId = String(id);

    setReassigningImage(targetImage.url);
    try {
      await api.post(`/admin/question-bank/import/${importId}/reassign-image`, {
        image_url: targetImage.url,
        question_id: newQId,
      });
      await fetchExtractedImages();

      if (newQId === currentId || prevQId === currentId) {
        await fetchQuestion();

        // Force imageUrls to match the reassignment outcome regardless of whether
        // the backend updated question.image_url in the same request.
        if (newQId === currentId) {
          // Image is now assigned to this question — ensure it appears in the form
          setImageUrls((prev) =>
            prev.includes(targetImage.url) ? prev : [...prev, targetImage.url]
          );
        } else if (prevQId === currentId) {
          // Image was removed from this question — ensure it is gone from the form
          setImageUrls((prev) => prev.filter((u) => u !== targetImage.url));
        }

        setImagesLoaded(false); // force re-fetch of panel on next open
      }

      setReassignDialogOpen(false);
      setReassignTargetImage(null);
      toast.success("Image reassigned");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reassign image");
    } finally {
      setReassigningImage(null);
    }
  };

  // Quick-assign an extracted image to the current question without opening the dialog
  const handleAssignToThisQuestion = async (img) => {
    const currentId = String(id);
    setReassigningImage(img.url);
    try {
      await api.post(`/admin/question-bank/import/${importId}/reassign-image`, {
        image_url: img.url,
        question_id: currentId,
      });
      // Add to imageUrls immediately (the backend may not update question.image_url)
      setImageUrls((prev) => (prev.includes(img.url) ? prev : [...prev, img.url]));
      await fetchExtractedImages();
      await fetchQuestion();
      // Re-enforce after fetch in case server response didn't include it
      setImageUrls((prev) => (prev.includes(img.url) ? prev : [...prev, img.url]));
      setImagesLoaded(false);
      toast.success("Image assigned to this question");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to assign image");
    } finally {
      setReassigningImage(null);
    }
  };

  // Fully remove an extracted image from the import:
  // strips it from marker_images, clears it from question records, and deletes the file.
  const handleRemoveExtractedImage = async (img) => {
    setRemovingExtractedImage(img.url);
    try {
      await api.post(`/admin/question-bank/import/${importId}/remove-image`, { image_url: img.url });
      // If it was assigned to this question, remove from the main image section too
      if (img.assigned_question_id != null && String(img.assigned_question_id) === String(id)) {
        setImageUrls((prev) => prev.filter((u) => u !== img.url));
      }
      await fetchExtractedImages();
      toast.success("Image removed");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove image");
    } finally {
      setRemovingExtractedImage(null);
    }
  };

  const handleApplyChanges = async () => {
    if (!importId) return;
    setApplyingChanges(true);
    setApplyChangesResult(null);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/apply-updates`, { question_id: id });
      const d = res.data?.data || {};
      setApplyChangesResult(d);
      const parts = [];
      if ((d.meta_updated ?? 0) > 0) parts.push(`${d.meta_updated} diagram type(s) set`);
      if ((d.image_fixed ?? 0) > 0) parts.push(`${d.image_fixed} image(s) corrected`);
      if ((d.diagrams_generated ?? 0) > 0) parts.push(`${d.diagrams_generated} diagram(s) generated`);
      if ((d.text_updated ?? 0) > 0) parts.push(`${d.text_updated} question text(s) updated`);
      if ((d.answer_data_updated ?? 0) > 0) parts.push(`${d.answer_data_updated} answer data updated`);
      if ((d.decimal_fixed ?? 0) > 0) parts.push(`${d.decimal_fixed} decimal(s) normalised`);
      if ((d.options_fixed ?? 0) > 0) parts.push(`${d.options_fixed} multiple-select option(s) fixed`);
      if (parts.length === 0) parts.push("already up-to-date");
      toast.success(`Apply Updates (this question): ${parts.join(", ")}.`);
      setImagesLoaded(false);
      await fetchQuestion();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to apply changes");
    } finally {
      setApplyingChanges(false);
    }
  };

  const handleImageReorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const newUrls = [...imageUrls];
    const [moved] = newUrls.splice(fromIdx, 1);
    newUrls.splice(toIdx, 0, moved);
    setImageUrls(newUrls);
    if (isEdit && id) {
      api.patch(`/admin/question-bank/${id}`, { image_urls: newUrls }).catch(() => {});
    }
  };

  const buildPayload = () => {
    const payload = {
      question_text: questionText,
      type,
      ...(!isEdit ? { source_type: 'past-paper' } : {}),
      category_id: categoryId || null,
      difficulty,
      default_marks: parseInt(defaultMarks) || 1,
      default_duration_sec: defaultDurationSec ? parseInt(defaultDurationSec) : null,
      image_url:  imageUrls[0] || null,
      image_urls: imageUrls.length > 0 ? imageUrls : null,
      image_description: imageDescription || null,
      topic: topic || null,
      topic_ids: topicIds,
      passage_type: passageType || null,
      explanation: explanation || null,
      section_title: sectionTitle.trim() || null,
      section_instructions: sectionInstructions.trim() || null,
      is_sample_question: isSampleQuestion,
      mcq_recommended: mcqRecommended,
      mcq_recommended_note: mcqRecommended ? (mcqRecommendedNote.trim() || null) : null,
    };

    switch (type) {
      case "multiple-choice":
        payload.options = options;
        payload.correct_option_index = correctOptionIndex;
        break;
      case "multiple-select":
        payload.options = options;
        payload.correct_option_indices = correctOptionIndices;
        break;
      case "true-false":
        payload.correct_answer = trueFalseAnswer;
        break;
      case "fill-in-the-blank":
      case "multi-fill":
        payload.correct_blanks = blanks;
        payload.type = (blanks?.length ?? 0) >= 2 ? "multi-fill" : "fill-in-the-blank";
        if (blankLabels.length > 0) payload.blank_labels = blankLabels;
        if (blankMarks.length > 0) payload.blank_marks = blankMarks;
        if (blankLabelPositions.length > 0) payload.blank_label_positions = blankLabelPositions;
        if (diagramType) payload.diagram_type = diagramType;
        if (hubValue) payload.hub_value = hubValue;
        // Combined part: keep the attached drawing/shade spec (e.g. sketch + typed perimeter/area).
        payload.solution_geometry = interactiveSpec ? { interactive: interactiveSpec } : null;
        if (overlayLayout?.rows_interchangeable) payload.overlay_layout = { rows_interchangeable: true, ...(overlayLayout?.interchangeable_row_indices?.length ? { interchangeable_row_indices: overlayLayout.interchangeable_row_indices } : {}) };
        if (fillMarksMode === "fixed") {
          const fixedTotal = parseInt(fillFixedTotal, 10) || 1;
          payload.default_marks = fixedTotal;
          payload.answer_data = {
            marks_mode: "fixed",
            marks_total: fixedTotal,
            ...(blankLabelPositions.length > 0 ? { blank_label_positions: blankLabelPositions } : {}),
          };
        }
        break;
      case "table":
        payload.correct_blanks = blanks;
        payload.type = "table";
        if (blankLabels.length > 0) payload.blank_labels = blankLabels;
        if (blankMarks.length > 0) payload.blank_marks = blankMarks;
        if (tableLayout?.grid?.length > 0) payload.table_layout = tableLayout;
        // "The rows may be written in any order" lives on overlay_layout for a table too.
        if (overlayLayout?.rows_interchangeable) payload.overlay_layout = { rows_interchangeable: true, ...(overlayLayout?.interchangeable_row_indices?.length ? { interchangeable_row_indices: overlayLayout.interchangeable_row_indices } : {}) };
        if (fillMarksMode === "fixed") {
          const fixedTotal = parseInt(fillFixedTotal, 10) || 1;
          payload.default_marks = fixedTotal;
          payload.answer_data = { marks_mode: "fixed", marks_total: fixedTotal };
        }
        break;
      case "image-fill":
        payload.correct_blanks = blanks;
        payload.type = "image-fill";
        if (blankLabels.length > 0) payload.blank_labels = blankLabels;
        if (blankMarks.length > 0) payload.blank_marks = blankMarks;
        if (overlayLayout?.image_url || overlayLayout?.mode === "inline" || overlayLayout?.rows_interchangeable) payload.overlay_layout = overlayLayout;
        payload.solution_geometry = interactiveSpec ? { interactive: interactiveSpec } : null;
        if (fillMarksMode === "fixed") {
          const fixedTotal = parseInt(fillFixedTotal, 10) || 1;
          payload.default_marks = fixedTotal;
          payload.answer_data = { marks_mode: "fixed", marks_total: fixedTotal };
        }
        break;
      case "matching":
        payload.options = options;
        break;
      case "ordering":
        payload.options = options;
        break;
      case "essay":
        payload.model_answer = modelAnswer;
        break;
      case "short-answer":
        payload.correct_answer_text = shortAnswer;
        break;
      case "drawing":
        payload.correct_answer_text = shortAnswer;
        payload.correct_solution_image_url = correctSolutionImageUrl || null;
        payload.solution_geometry = interactiveSpec ? { interactive: interactiveSpec } : null;
        break;
      case "grouped": {
        const buildSqPayload = (sq) => {
          const p = {
            question_text: sq.question_text,
            type: sq.type,
            image_url: sq.image_url || null,
            // Student rendering prefers image_urls over image_url, and the server keeps the stored
            // image_urls when the key is absent — send both so removing/replacing the sub image
            // can't leave a stale image_urls entry behind to win and paint the old figure.
            image_urls: sq.image_url ? [sq.image_url] : null,
            // Always sent, so emptying the field in the editor clears the field on the server.
            image_description: String(sq.image_description || "").trim() || null,
            default_marks: parseInt(sq.default_marks) || 1,
            explanation: sq.explanation || null,
            hide_shared_context: !!sq.hide_shared_context,
            mcq_recommended: !!sq.mcq_recommended,
            mcq_recommended_note: sq.mcq_recommended ? (String(sq.mcq_recommended_note || "").trim() || null) : null,
          };
          if (sq.id) p.id = sq.id;
          switch (sq.type) {
            case "multiple-choice":
              p.options = sq.options;
              p.correct_option_index = sq.correct_option_index;
              break;
            case "multiple-select":
              p.options = sq.options;
              p.correct_option_indices = sq.correct_option_indices;
              break;
            case "true-false":
              p.correct_answer = sq.trueFalseAnswer;
              break;
            case "fill-in-the-blank":
            case "multi-fill":
              p.correct_blanks = sq.blanks;
              p.type = (sq.blanks?.length ?? 0) >= 2 ? "multi-fill" : "fill-in-the-blank";
              if (sq.blankLabels?.length > 0) p.blank_labels = sq.blankLabels;
              if (sq.blankMarks?.length > 0) p.blank_marks = sq.blankMarks;
              if (sq.blankLabelPositions?.length > 0) p.blank_label_positions = sq.blankLabelPositions;
              if (sq.overlayLayout?.rows_interchangeable) p.overlay_layout = { rows_interchangeable: true, ...(sq.overlayLayout?.interchangeable_row_indices?.length ? { interchangeable_row_indices: sq.overlayLayout.interchangeable_row_indices } : {}) };
              // Combined part: keep the attached drawing/shade spec (e.g. sketch + typed perimeter/area).
              p.solution_geometry = sq.interactiveSpec ? { interactive: sq.interactiveSpec } : null;
              if (sq.marksMode === "fixed") {
                const ft = parseInt(sq.fixedTotal, 10) || 1;
                p.answer_data = { ...(p.answer_data || {}), marks_mode: "fixed", marks_total: ft };
                p.default_marks = ft;
              }
              break;
            case "table":
              p.correct_blanks = sq.blanks;
              if (sq.blankLabels?.length > 0) p.blank_labels = sq.blankLabels;
              if (sq.blankMarks?.length > 0) p.blank_marks = sq.blankMarks;
              if (sq.tableLayout?.grid?.length > 0) p.table_layout = sq.tableLayout;
              if (sq.marksMode === "fixed") {
                const ft = parseInt(sq.fixedTotal, 10) || 1;
                p.answer_data = { ...(p.answer_data || {}), marks_mode: "fixed", marks_total: ft };
                p.default_marks = ft;
              }
              break;
            case "image-fill":
              p.correct_blanks = sq.blanks;
              if (sq.blankLabels?.length > 0) p.blank_labels = sq.blankLabels;
              if (sq.blankMarks?.length > 0) p.blank_marks = sq.blankMarks;
              if (sq.overlayLayout?.image_url || sq.overlayLayout?.mode === "inline" || sq.overlayLayout?.rows_interchangeable) p.overlay_layout = sq.overlayLayout;
              p.solution_geometry = sq.interactiveSpec ? { interactive: sq.interactiveSpec } : null;
              if (sq.marksMode === "fixed") {
                const ft = parseInt(sq.fixedTotal, 10) || 1;
                p.answer_data = { ...(p.answer_data || {}), marks_mode: "fixed", marks_total: ft };
                p.default_marks = ft;
              }
              break;
            case "matching":
              p.options = sq.options;
              break;
            case "ordering":
              p.options = sq.options;
              break;
            case "essay":
              p.model_answer = sq.modelAnswer;
              break;
            case "short-answer":
              p.correct_answer_text = sq.shortAnswer;
              break;
            case "drawing":
              p.correct_answer_text = sq.shortAnswer;
              p.correct_solution_image_url = sq.correctSolutionImageUrl || null;
              p.solution_geometry = sq.interactiveSpec ? { interactive: sq.interactiveSpec } : null;
              break;
            default:
              break;
          }
          // Typed sub-answers run through the same fuzzy tiers as a standalone question — a grouped part
          // ("write an expression for the perimeter") needs the exact-match promise just as much.
          if (["fill-in-the-blank", "multi-fill", "short-answer", "table", "image-fill"].includes(sq.type)) {
            p.exact_match = !!sq.exactMatch;
          }
          if (sq.sub_questions?.length > 0) {
            p.sub_questions = sq.sub_questions.map(buildSqPayload);
          }
          return p;
        };
        payload.sub_label_type = subLabelType;
        if (groupedMarksMode === "fixed") {
          const fixedTotal = parseInt(groupedFixedTotal, 10) || 1;
          payload.default_marks = fixedTotal;
          payload.answer_data = { marks_mode: "fixed", marks_total: fixedTotal };
          payload.sub_questions = subQuestions.map((sq) => ({ ...buildSqPayload(sq), default_marks: 1 }));
        } else {
          payload.sub_questions = subQuestions.map(buildSqPayload);
          payload.default_marks = Math.max(1, sumSubQuestionMarks(subQuestions));
        }
        payload.answer_data = { ...(payload.answer_data || {}), extend_parts: groupedExtend };
        break;
      }
      default:
        break;
    }

    if (["fill-in-the-blank", "multi-fill", "short-answer", "table", "image-fill"].includes(type)) {
      payload.semantic_match = !!semanticMatch;
      payload.exact_match = !!exactMatch;
    }

    if (type !== "grouped") {
      payload.force_measurement_tool = forceMeasurementTool === "auto" ? null : forceMeasurementTool;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation — text is optional when the question is carried by an image/visual
    // (uploaded image, image-fill overlay, or a table/column-sum grid).
    const isEmptyQuestion = !questionText.replace(/<[^>]*>/g, '').trim();
    const hasStemVisual =
      imageUrls.length > 0 ||
      !!overlayLayout?.image_url ||
      (type === "table" && (tableLayout?.grid?.length || 0) > 0);
    if (isEmptyQuestion && type !== 'grouped' && !hasStemVisual) {
      setQuestionTextError("Add question text or an image");
      return;
    }
    setQuestionTextError("");

    if (type === "grouped") {
      if (subQuestions.length === 0) {
        toast.error("At least one sub-question is required for a grouped question");
        return;
      }
      for (const sq of subQuestions) {
        const subPlain = (sq.question_text || "").replace(/<[^>]*>/g, "").trim();
        const sqHasImage =
          sq.image_url ||
          sq.image_urls?.length > 0 ||
          !!sq.overlayLayout?.image_url ||
          (sq.type === "table" && (sq.tableLayout?.grid?.length || 0) > 0);
        if (!subPlain && !sqHasImage) {
          toast.error("All sub-questions must have question text or an image");
          return;
        }
      }
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.put(`/admin/question-bank/${id}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/admin/question-bank", payload);
        toast.success("Question created");
      }
      navigate(returnTo);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save question";
      const errors = err.response?.data?.errors;
      if (errors) {
        const firstError = Object.values(errors)[0];
        toast.error(Array.isArray(firstError) ? firstError[0] : firstError);
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const renderTypeSpecificFields = () => {
    switch (type) {
      case "multiple-choice":
        return (
          <McqOptions
            options={options}
            setOptions={setOptions}
            correctIndex={correctOptionIndex}
            setCorrectIndex={setCorrectOptionIndex}
          />
        );
      case "multiple-select":
        return (
          <McqOptions
            options={options}
            setOptions={setOptions}
            correctIndices={correctOptionIndices}
            setCorrectIndices={setCorrectOptionIndices}
            isMultiSelect
          />
        );
      case "true-false":
        return <TrueFalseOptions value={trueFalseAnswer} onChange={setTrueFalseAnswer} />;
      case "fill-in-the-blank":
        return (
          <>
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
              showRowsInterchangeable
              rowsInterchangeable={overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, rows_interchangeable: v })}
              interchangeableRowIndices={overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_indices: v })}
              showRowsInterchangeable
              rowsInterchangeable={overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, rows_interchangeable: v })}
              interchangeableRowIndices={overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_indices: v })}
              showSemanticMatch
              semanticMatch={semanticMatch}
              onSemanticMatchChange={setSemanticMatch}
              exactMatch={exactMatch}
              onExactMatchChange={setExactMatch}
            />
            <FillInBlankEditor
              blanks={blanks}
              setBlanks={setBlanks}
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabelPositions={blankLabelPositions}
              setBlankLabelPositions={setBlankLabelPositions}
              fillMarksMode={fillMarksMode}
              fillFixedTotal={fillFixedTotal}
            />
            <CombinedDrawingAnswer
              interactiveSpec={interactiveSpec}
              setInteractiveSpec={setInteractiveSpec}
              attachedImages={imageUrls}
            />
          </>
        );
      case "multi-fill":
        return (
          <>
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
              showRowsInterchangeable
              rowsInterchangeable={overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, rows_interchangeable: v })}
              interchangeableRowIndices={overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_indices: v })}
              showSemanticMatch
              semanticMatch={semanticMatch}
              onSemanticMatchChange={setSemanticMatch}
              exactMatch={exactMatch}
              onExactMatchChange={setExactMatch}
            />
            <FillInBlankEditor
              blanks={blanks}
              setBlanks={setBlanks}
              isMulti
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabelPositions={blankLabelPositions}
              setBlankLabelPositions={setBlankLabelPositions}
              fillMarksMode={fillMarksMode}
              fillFixedTotal={fillFixedTotal}
            />
            <CombinedDrawingAnswer
              interactiveSpec={interactiveSpec}
              setInteractiveSpec={setInteractiveSpec}
              attachedImages={imageUrls}
            />
          </>
        );
      case "table":
        return (
          <>
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
              exactMatch={exactMatch}
              onExactMatchChange={setExactMatch}
            />
            <TableFillEditor
              blanks={blanks}
              setBlanks={setBlanks}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              tableLayout={tableLayout}
              setTableLayout={setTableLayout}
              showMathTools
            />
          </>
        );
      case "image-fill":
        return (
          <>
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
              showRowsInterchangeable
              rowsInterchangeable={overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, rows_interchangeable: v })}
              interchangeableRowIndices={overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_indices: v })}
              blanksInterchangeable={overlayLayout?.blanks_interchangeable}
              onBlanksInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, blanks_interchangeable: v })}
              exactMatch={exactMatch}
              onExactMatchChange={setExactMatch}
            />
            <ImageOverlayFillEditor
              blanks={blanks}
              setBlanks={setBlanks}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              overlayLayout={overlayLayout}
              setOverlayLayout={setOverlayLayout}
              attachedImages={imageUrls}
            />
            <CombinedDrawingAnswer
              interactiveSpec={interactiveSpec}
              setInteractiveSpec={setInteractiveSpec}
              attachedImages={imageUrls}
            />
          </>
        );
      case "matching":
        return <MatchingEditor options={options} setOptions={setOptions} />;
      case "ordering":
        return <OrderingEditor options={options} setOptions={setOptions} />;
      case "essay":
        return <EssayAnswer value={modelAnswer} onChange={setModelAnswer} showMathTools />;
      case "short-answer":
        return (
          <>
            <FormControlLabel
              sx={{ mb: 1 }}
              control={<Checkbox checked={!!semanticMatch} onChange={(e) => setSemanticMatch(e.target.checked)} size="small" disabled={!!exactMatch} />}
              label={
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
                  Grade with AI (accept answers phrased differently)
                </Typography>
              }
            />
            <FormControlLabel
              sx={{ mb: 1, display: "block" }}
              control={<Checkbox checked={!!exactMatch} onChange={(e) => { setExactMatch(e.target.checked); if (e.target.checked) setSemanticMatch(false); }} size="small" />}
              label={
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
                  Answer must be written exactly like this (no near-enough matching, no AI) — e.g. an
                  expression such as “2(3x + 5) + 2(7x + 2)”, where a missing “+” is wrong
                </Typography>
              }
            />
            <ShortAnswerEditor value={shortAnswer} onChange={setShortAnswer} showMathTools />
          </>
        );
      case "drawing": {
        const drawMode = interactiveSpec?.kind || "free";
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControl size="small" sx={{ maxWidth: 320 }}>
              <InputLabel>Answer mode</InputLabel>
              <Select
                label="Answer mode"
                value={drawMode}
                onChange={(e) => setInteractiveSpec(e.target.value === "free" ? null : { kind: e.target.value })}
              >
                <MenuItem value="free">Free draw (AI vision)</MenuItem>
                <MenuItem value="shade">Shade regions (tap to fill)</MenuItem>
                <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                <MenuItem value="cut-drag">Cut shape &amp; drag pieces (rearrange)</MenuItem>
                <MenuItem value="segments">Join the crosses (nodes → lines)</MenuItem>
                <MenuItem value="polygon">Draw shape by area (triangle / square)</MenuItem>
                <MenuItem value="glyph">Place / reflect / rotate letters</MenuItem>
                <MenuItem value="drag-shape">Drag-copy shape (translation)</MenuItem>
                <MenuItem value="reflect">Reflect shape (auto-computed)</MenuItem>
                <MenuItem value="rotate">Rotate shape (auto-computed)</MenuItem>
                <MenuItem value="clock">Clock hands (set the time)</MenuItem>
                <MenuItem value="marker">Arrow / bar marker (value on axis)</MenuItem>
                <MenuItem value="pictogram">Pictogram (complete the row of icons)</MenuItem>
              </Select>
            </FormControl>
            {drawMode === "free" ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Free draw: student draws on the pad; graded against a generated answer diagram via AI
                  vision. Interactive modes above grade instantly with no AI.
                </Typography>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>Answer description (optional)</Typography>
                  <Box sx={{ '& .ql-toolbar': { borderRadius: '6px 6px 0 0' }, '& .ql-container': { minHeight: 90, borderRadius: '0 0 6px 6px' } }}>
                    <QuillR2Editor
                      value={shortAnswer}
                      onChange={(html) => setShortAnswer(normalizeQuillValue(html))}
                      modules={EXPL_QUILL_MODULES}
                      formats={EXPL_QUILL_FORMATS}
                      placeholder="e.g. Reflect the triangle across the line; vertices mirror onto the opposite side. Add an image of the worked solution if helpful."
                      style={{ minHeight: 110 }}
                      showMathTools
                    />
                  </Box>
                </Box>
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={generatingAnswerImage ? <CircularProgress size={14} /> : <AutoAwesome />}
                    onClick={handleGenerateAnswerImage}
                    disabled={generatingAnswerImage || !isEdit || !id}
                    sx={{ borderColor: "#0ea5e9", color: "#0ea5e9", textTransform: "none" }}
                  >
                    {generatingAnswerImage ? "Generating…" : "Generate Answer Diagram"}
                  </Button>
                </Box>
                {correctSolutionImageUrl ? (
                  <Box sx={{ display: "inline-flex", position: "relative", alignSelf: "flex-start" }}>
                    <img
                      src={correctSolutionImageUrl}
                      alt="Correct answer diagram"
                      style={{ maxWidth: 320, maxHeight: 260, borderRadius: 4, border: "1px solid #e0e0e0", objectFit: "contain" }}
                      onError={(e) => { e.target.style.opacity = "0.3"; }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setCorrectSolutionImageUrl("")}
                      sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(255,255,255,0.85)", color: "#f44336", p: 0.25 }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    No answer diagram yet — attach the question image, save, then click Generate.
                  </Typography>
                )}
              </>
            ) : (
              <InteractiveDiagramEditor
                kind={drawMode}
                spec={interactiveSpec}
                onChange={setInteractiveSpec}
                imageUrl={imageUrls[0] || ""}
              />
            )}
          </Box>
        );
      }
      case "grouped":
        return null;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(returnTo)}
          sx={{ color: "#666" }}
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a1a", flex: 1 }}>
          {isEdit ? "Edit Question" : "Create Question"}
        </Typography>
        {importId && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => setMarkdownDialogOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Source markdown
          </Button>
        )}
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            background: "#fff"
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#333" }}>
            Question Details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444" }}>
                  {type === "grouped" ? "Passage / shared text — source editor *" : "Question text — source editor *"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                  {type === "grouped" && (
                    <>
                      <Tooltip title="Word-wrap the passage and number every 5th line — then tweak the {L…} markers by hand">
                        <Button size="small" variant="outlined" onClick={handleFormInsertLineNumbers} sx={{ py: 0.25, px: 1, fontSize: "0.7rem", textTransform: "none" }}>
                          Insert line numbers
                        </Button>
                      </Tooltip>
                      <Tooltip title="Place cursor in the passage, type a line number, then click Insert to add {L#} at that position">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={formManualLineNum}
                            onChange={(e) => setFormManualLineNum(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleFormInsertManualLineNum(); }}
                            placeholder="L#"
                            inputProps={{ min: 1, max: 999, style: { padding: "2px 6px", fontSize: "0.7rem", width: 44, textAlign: "center" } }}
                            sx={{ "& .MuiOutlinedInput-root": { height: 24 } }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleFormInsertManualLineNum}
                            disabled={!formManualLineNum || parseInt(formManualLineNum, 10) < 1}
                            sx={{ py: 0.25, px: 0.75, fontSize: "0.7rem", textTransform: "none", minWidth: 0 }}
                          >
                            Insert {formManualLineNum ? `{L${formManualLineNum}}` : "{L#}"}
                          </Button>
                        </Box>
                      </Tooltip>
                      {/\{L\d+\}/.test(questionText || "") && (
                        <Button size="small" variant="text" color="error" onClick={handleFormClearLineNumbers} sx={{ py: 0.25, px: 0.75, fontSize: "0.7rem", textTransform: "none" }}>
                          Clear
                        </Button>
                      )}
                    </>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    startIcon={questionLivePreview ? <VisibilityOffIcon sx={{ fontSize: "1rem !important" }} /> : <VisibilityIcon sx={{ fontSize: "1rem !important" }} />}
                    onClick={() => setQuestionLivePreview((v) => !v)}
                    sx={{ textTransform: "none", fontSize: "0.8rem" }}
                  >
                    {questionLivePreview ? "Hide" : "Show"} live preview
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    flex: questionLivePreview ? "1 1 45%" : "1 1 100%",
                    minWidth: 0,
                    "& .quill": {
                      background: "#fff",
                      borderRadius: 1,
                    },
                    "& .ql-container": {
                      minHeight: "150px",
                      fontSize: "14px",
                      borderRadius: "0 0 4px 4px",
                    },
                    "& .ql-toolbar": {
                      borderRadius: "4px 4px 0 0",
                      background: "#f9f9f9",
                      flexWrap: "wrap",
                    },
                    "& .ql-editor": {
                      minHeight: "150px",
                    },
                    ...(questionTextError && {
                      "& .ql-toolbar": { borderColor: "#d32f2f" },
                      "& .ql-container": { borderColor: "#d32f2f" },
                    }),
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {type === "grouped" ? "Passage / shared text (what students see)" : "Question text (what students see)"}
                    </Typography>
                    {isEdit && id && type !== "grouped" && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={improving ? <CircularProgress size={12} /> : <AutoFixHigh sx={{ fontSize: "0.9rem !important" }} />}
                        onClick={handleAiImprove}
                        disabled={improving || generatingExplanation}
                        sx={{ py: 0.25, px: 1, fontSize: "0.75rem", textTransform: "none" }}
                      >
                        {improving ? "Working…" : "AI Improve"}
                      </Button>
                    )}
                  </Box>
                  <QuillR2Editor
                    ref={type === "grouped" ? passageFormQuillRef : undefined}
                    value={questionText}
                    onChange={(content) => {
                      setQuestionText(content);
                      if (questionTextError && content.replace(/<[^>]*>/g, "").trim()) {
                        setQuestionTextError("");
                      }
                    }}
                    onChangeSelection={type === "grouped" ? (range) => { if (range) savedPassageFormSelectionRef.current = range; } : undefined}
                    modules={type === "grouped" ? PASSAGE_QUILL_MODULES : QUILL_MODULES}
                    formats={type === "grouped" ? PASSAGE_QUILL_FORMATS : QUILL_FORMATS}
                    placeholder={type === "grouped" ? "Paste the full reading passage here..." : "Enter your question text here. Use $...$ for inline LaTeX."}
                    showMathTools
                  />
                </Box>
                {questionLivePreview && (
                  <Paper
                    variant="outlined"
                    sx={{
                      flex: "1 1 45%",
                      minWidth: { xs: "100%", lg: 280 },
                      p: 1.5,
                      bgcolor: "grey.50",
                      maxHeight: 360,
                      overflow: "auto",
                      "& mjx-container": { fontSize: "0.95rem" },
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                      Live preview (MathJax — same rendering as the paper view)
                    </Typography>
                    {(questionText || "").replace(/<[^>]*>/g, "").trim() ? (
                      <HtmlWithLatex html={stripTagsKeepLatex(questionText || "")} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Type or paste text to see the formatted preview.
                      </Typography>
                    )}
                  </Paper>
                )}
              </Box>
              {questionTextError && (
                <FormHelperText error>{questionTextError}</FormHelperText>
              )}
              <Typography variant="caption" sx={{ color: "#666", mt: 0.5, display: "block" }}>
                LaTeX is supported using $...$ or $$...$$. For &quot;Fill in the Blank&quot;, use ____ (four underscores) for the blank.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <QuestionTypeSelector
                value={type}
                onChange={(e) => handleTypeChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CategoryTreeSelect
                value={categoryId}
                onChange={setCategoryId}
                label="Category"
                noneLabel="Uncategorized"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={difficulty}
                  label="Difficulty"
                  onChange={(e) => setDifficulty(e.target.value)}
                >
                  <MenuItem value="Easy">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: "50%", 
                        bgcolor: "#4caf50" 
                      }} />
                      Easy
                    </Box>
                  </MenuItem>
                  <MenuItem value="Medium">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: "50%", 
                        bgcolor: "#ff9800" 
                      }} />
                      Medium
                    </Box>
                  </MenuItem>
                  <MenuItem value="Hard">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: "50%", 
                        bgcolor: "#f44336" 
                      }} />
                      Hard
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              {type === "grouped" ? (
                <TextField
                  fullWidth
                  label="Total marks"
                  type="number"
                  value={groupedMarksMode === "fixed" ? (parseInt(groupedFixedTotal, 10) || 1) : Math.max(1, sumSubQuestionMarks(subQuestions))}
                  InputProps={{ readOnly: true }}
                  helperText={groupedMarksMode === "fixed" ? `Fixed total (${groupedFixedTotal}/${subQuestions.length} per part)` : "Sum of sub-question marks"}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Default Marks"
                  type="number"
                  value={defaultMarks}
                  onChange={(e) => setDefaultMarks(e.target.value)}
                  inputProps={{ min: 1 }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Duration (seconds)"
                type="number"
                value={defaultDurationSec}
                onChange={(e) => setDefaultDurationSec(e.target.value)}
                inputProps={{ min: 1 }}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSampleQuestion}
                    onChange={(e) => setIsSampleQuestion(e.target.checked)}
                    color="primary"
                  />
                }
                label="Sample question"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={mcqRecommended}
                    onChange={(e) => setMcqRecommended(e.target.checked)}
                    color="primary"
                  />
                }
                label="Recommend convert to MCQ (hard to answer online)"
              />
              {mcqRecommended && (
                <TextField
                  size="small"
                  fullWidth
                  label="Note shown to students (optional)"
                  value={mcqRecommendedNote}
                  onChange={(e) => setMcqRecommendedNote(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  sx={{ mt: 0.5 }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Passage type</InputLabel>
                <Select
                  value={passageType}
                  label="Passage type"
                  onChange={(e) => setPassageType(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="comprehension">Comprehension</MenuItem>
                  <MenuItem value="context">Context</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TopicTreeSelect
                label="Topics"
                value={topicIds}
                onChange={(ids) => setTopicIds(ids.map(Number))}
                catalog={topicCatalog}
                yearId={topicYearId}
                onYearChange={(y) => { setTopicYearId(y); setTopicIds([]); }}
                schoolYears={schoolYears}
                disabled={!subjectId || topicCatalog.length === 0}
              />
              {!subjectId && (
                <Typography variant="caption" color="text.secondary">
                  Set a subject (via category/import) to enable topic tagging
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Section Fields */}
        <Paper
          elevation={0}
          sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2, background: "#fff" }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#333" }}>
            Section (Optional)
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Section title"
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              placeholder="e.g. Section A — Reading Comprehension"
              helperText="Displayed as a section header above this question"
            />
            <TextField
              fullWidth
              size="small"
              label="Section instructions"
              value={sectionInstructions}
              onChange={(e) => setSectionInstructions(e.target.value)}
              placeholder="Instructions shown at the start of this section (optional)"
              multiline
              minRows={2}
            />
          </Box>
        </Paper>

        {/* Image Upload Section */}
        <Paper
          elevation={0}
          onDragOver={(e) => {
            if (draggedExtractedImageRef.current) {
              e.preventDefault();
              setIsDropOverImageArea(true);
            }
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setIsDropOverImageArea(false);
          }}
          onDrop={async (e) => {
            const img = draggedExtractedImageRef.current;
            if (!img) return;
            e.preventDefault();
            setIsDropOverImageArea(false);
            draggedExtractedImageRef.current = null;
            await handleAssignToThisQuestion(img);
          }}
          sx={{
            p: 3,
            mb: 3,
            border: isDropOverImageArea ? "2px dashed #6366f1" : "1px solid #e0e0e0",
            borderRadius: 2,
            background: isDropOverImageArea ? "rgba(99,102,241,0.04)" : "#fff",
            transition: "border 0.15s, background 0.15s",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Images and diagrams (optional)
            </Typography>
            <GenerateVisualButton
              questionText={questionText}
              subjectId={subjectId}
              visual={aiVisualSpec}
              disabled={isImageProcessing}
              onGenerated={(url, spec) => {
                setImageUrls((prev) => [...prev, url]);
                setAiVisualSpec(spec || null);
              }}
            />
            {isDropOverImageArea && (
              <Typography variant="caption" sx={{ color: "#6366f1", fontWeight: 600 }}>
                Drop to assign image here
              </Typography>
            )}
            {importId && !isDropOverImageArea && (
              <Typography variant="caption" color="text.secondary">
                Drag from Extracted Images below to assign
              </Typography>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Diagram description — shown and used when generating diagram (same style as Import Review) */}
          <Box
            sx={{
              mb: 2,
              border: "1px dashed",
              borderColor: "primary.300",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "transparent",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                bgcolor: "primary.50",
                borderBottom: "1px dashed",
                borderColor: "primary.200",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.700", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Diagram
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                (description for reference — used when you click &ldquo;Generate Diagram&rdquo;)
              </Typography>
            </Box>
            <Box sx={{ p: 1.5 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                size="small"
                label="Image description (optional)"
                placeholder="e.g. Hub-and-spoke diagram: central oval = 6. Top-left: ½ + □. Top-right: 3.5 + □. Bottom-left: ⅖ × □. Bottom-right: 6 ÷ □."
                value={imageDescription}
                onChange={(e) => setImageDescription(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontStyle: "italic",
                    fontSize: "0.875rem",
                  },
                }}
                InputProps={{
                  endAdornment: imageDescription ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setImageDescription("")} title="Delete description">
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              {/* Warn when no description and no blank_labels — diagram will be AI-guessed */}
              {!imageDescription && blankLabels.length === 0 && (type !== "fill-in-the-blank" && type !== "multi-fill") && (
                <Box sx={{ mt: 1, px: 1, py: 0.75, bgcolor: "warning.50", border: "1px solid", borderColor: "warning.200", borderRadius: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="caption" sx={{ color: "warning.800", lineHeight: 1.5 }}>
                    No description or expression labels found — the diagram will be AI-guessed from question text. For an exact diagram, type a description above.
                  </Typography>
                </Box>
              )}

              {/* ── Spoke labels editor (fill / multi-fill only) ─────────────── */}
              {(type === "fill-in-the-blank" || type === "multi-fill") && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.700" }}>
                      Spoke Labels
                      <Typography component="span" variant="caption" sx={{ color: "text.disabled", ml: 0.5, fontWeight: 400 }}>
                        — one expression per box, e.g. <code style={{ fontFamily: "monospace", background: "#e3f2fd", padding: "0 3px", borderRadius: 3 }}>½ + □ = 6</code>
                      </Typography>
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Add sx={{ fontSize: "0.85rem !important" }} />}
                      onClick={addBlankLabel}
                      sx={{ py: 0, minWidth: 0, fontSize: "0.75rem", color: "primary.700" }}
                    >
                      Add
                    </Button>
                  </Box>

                  {blankLabels.length === 0 ? (
                    <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
                      No spoke labels — diagram will be AI-guessed. Add labels above for an exact hub-spoke layout.
                    </Typography>
                  ) : (
                    blankLabels.map((label, i) => (
                      <Box key={i} sx={{ display: "flex", gap: 0.75, mb: 0.75, alignItems: "center" }}>
                        <TextField
                          size="small"
                          value={label}
                          onChange={(e) => updateBlankLabel(i, e.target.value)}
                          placeholder={i === 0 ? "e.g. ½ + □ = 6" : "e.g. 3.5 + □ = 6"}
                          fullWidth
                          sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem", fontFamily: "monospace" } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeBlankLabel(i)}
                          sx={{ color: "error.light", flexShrink: 0 }}
                          title="Remove label"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))
                  )}

                  {/* Hub value — editable; shown when hub-spoke (any variant) or when all labels have blanks */}
                  {isAnyHubSpoke(diagramType, blankLabels) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0 }}>
                        Hub value (centre oval):
                      </Typography>
                      <TextField
                        size="small"
                        value={hubValue}
                        onChange={(e) => setHubValue(e.target.value)}
                        placeholder="e.g. 6"
                        sx={{ maxWidth: 130, "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
                      />
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>
                        (auto-detected from labels if blank)
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          
          {/* ── Hub-spoke live preview (label-driven, no imageUrl required) ── */}
          {isAnyHubSpoke(diagramType, blankLabels) && blankLabels.length >= 2 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                  Live Preview
                </Typography>
                {isEdit && imageUrls.length === 0 && (
                  <Chip
                    label="No diagram saved — click Generate Diagram"
                    size="small"
                    sx={{ bgcolor: "warning.50", color: "warning.800", border: "1px solid", borderColor: "warning.200", fontSize: "0.72rem" }}
                  />
                )}
                {isEdit && imageUrls.length > 0 && (
                  <Chip
                    label="Diagram saved ✓ — regenerate to refresh"
                    size="small"
                    sx={{ bgcolor: "success.50", color: "success.800", border: "1px solid", borderColor: "success.200", fontSize: "0.72rem" }}
                  />
                )}
              </Box>
              {diagramType === "hub-spoke-outer" ? (
                <DiagramHubSpokeOuter
                  blankLabels={blankLabels}
                  hubValue={hubValue || undefined}
                  showAnswers
                  correctBlanks={blanks}
                />
              ) : (
                <DiagramHubSpoke
                  blankLabels={blankLabels}
                  hubValue={hubValue || undefined}
                  showAnswers
                  correctBlanks={blanks}
                />
              )}
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
            Students see these images with the question text.
          </Typography>

          <input
            type="file"
            accept="image/png, image/jpeg, image/webp"
            onChange={handleImageChange}
            style={{ display: "none" }}
            ref={fileInputRef}
            disabled={isImageProcessing}
          />

          {imageUrls.length > 0 && !isImageProcessing && !isAnyHubSpoke(diagramType, blankLabels) && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              {imageUrls.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ width: "100%", mb: 0.5 }}>
                  Drag to reorder — first image is primary
                </Typography>
              )}
              {imageUrls.map((url, idx) => (
                <Box
                  key={url}
                  draggable
                  onDragStart={() => setDragImageIndex(idx)}
                  onDragOver={(e) => { e.preventDefault(); setDragOverImageIndex(idx); }}
                  onDrop={() => { handleImageReorder(dragImageIndex, idx); setDragImageIndex(null); setDragOverImageIndex(null); }}
                  onDragEnd={() => { setDragImageIndex(null); setDragOverImageIndex(null); }}
                  sx={{
                    position: "relative",
                    border: dragOverImageIndex === idx && dragImageIndex !== idx
                      ? "2px dashed #6366f1"
                      : "1px solid #e0e0e0",
                    borderRadius: 1,
                    overflow: "hidden",
                    width: 100,
                    height: 100,
                    cursor: "grab",
                    opacity: dragImageIndex === idx ? 0.4 : 1,
                    transition: "opacity 0.15s, border 0.1s",
                  }}
                >
                  <img
                    src={url}
                    alt={`Diagram ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(url)}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: "rgba(255,255,255,0.85)",
                      color: "#f44336",
                      p: 0.25,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: idx === 0 ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.25)",
                      py: 0.25,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "white", fontSize: "0.6rem" }}>
                      {idx === 0 ? "Primary" : `#${idx + 1}`}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {imageUrls.length > 0 && !isImageProcessing && isAnyHubSpoke(diagramType, blankLabels) && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              {imageUrls.map((url, idx) => (
                <Box
                  key={url}
                  sx={{
                    position: "relative",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    overflow: "hidden",
                    width: 100,
                    height: 100,
                  }}
                >
                  <img
                    src={url}
                    alt={`Diagram ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => { e.target.style.opacity = "0.3"; }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(url)}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: "rgba(255,255,255,0.85)",
                      color: "#f44336",
                      p: 0.25,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    }}
                    title="Remove saved SVG"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  {idx === 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: "rgba(0,0,0,0.5)",
                        py: 0.25,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "white", fontSize: "0.6rem" }}>
                        SVG
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Box
            onDragOver={(e) => {
              if (e.dataTransfer?.types?.includes("Files")) {
                e.preventDefault();
                setImageDropActive(true);
              }
            }}
            onDragEnter={(e) => {
              if (e.dataTransfer?.types?.includes("Files")) setImageDropActive(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setImageDropActive(false);
            }}
            onDrop={async (e) => {
              const file = e.dataTransfer?.files?.[0];
              if (!file) return;
              e.preventDefault();
              e.stopPropagation();
              setImageDropActive(false);
              await uploadStemImageFile(file);
            }}
            onClick={() => !isImageProcessing && fileInputRef.current?.click()}
            sx={{
              mb: 2,
              border: "1px dashed",
              borderColor: imageDropActive ? "primary.main" : "divider",
              bgcolor: imageDropActive ? "primary.50" : "grey.50",
              borderRadius: 1.5,
              px: 2,
              py: 2.5,
              minHeight: 120,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              cursor: isImageProcessing ? "not-allowed" : "pointer",
              transition: "all 0.15s",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "center" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                Drag and drop image here (larger drop zone), or click to browse
              </Typography>
              <Typography variant="caption" color="text.disabled">
                You can also drag images from the Extracted Images panel below
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
            <Button
              variant="outlined"
              startIcon={isImageProcessing ? <CircularProgress size={20} /> : <CloudUpload />}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              disabled={isImageProcessing || isGeneratingDiagram}
              sx={{
                borderColor: "#6366f1",
                color: "#6366f1",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#4f46e5",
                  background: "rgba(99, 102, 241, 0.04)",
                },
              }}
            >
              {isImageProcessing ? "Processing..." : "Add Image"}
            </Button>
            {isEdit && (
              <Button
                variant="outlined"
                startIcon={isGeneratingDiagram ? <CircularProgress size={20} /> : <AutoAwesome />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateDiagram();
                }}
                disabled={isImageProcessing || isGeneratingDiagram}
                sx={{
                  borderColor: "#0ea5e9",
                  color: "#0ea5e9",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#0284c7",
                    background: "rgba(14, 165, 233, 0.04)",
                  },
                }}
              >
                {isGeneratingDiagram ? "Generating..." : "Generate Diagram"}
              </Button>
            )}
            {isImageProcessing && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Processing...
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* ── Extracted Images Panel (edit mode, import-linked questions only) ── */}
        {isEdit && importId && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2, background: "#fff" }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", userSelect: "none" }}
              onClick={toggleImagesPanel}
            >
              <PhotoLibrary sx={{ color: "text.secondary", fontSize: 20 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, color: "#333" }}>
                Extracted Images from Import
              </Typography>
              {imagesLoaded && (
                <Chip
                  size="small"
                  label={`${extractedImages.length} image${extractedImages.length !== 1 ? "s" : ""}${
                    extractedImages.filter((i) => !i.assigned_question_id).length > 0
                      ? ` (${extractedImages.filter((i) => !i.assigned_question_id).length} unassigned)`
                      : ""
                  }`}
                  color={extractedImages.filter((i) => !i.assigned_question_id).length > 0 ? "warning" : "success"}
                  variant="outlined"
                />
              )}
              {imagesLoading ? (
                <CircularProgress size={18} />
              ) : imagesOpen ? (
                <ExpandLessIcon sx={{ color: "text.secondary" }} />
              ) : (
                <ExpandMoreIcon sx={{ color: "text.secondary" }} />
              )}
            </Box>

            {imagesOpen && (
              <Box sx={{ mt: 2 }}>
                {imagesLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading images…</Typography>
                  </Box>
                ) : extractedImages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No extracted images found for this import.</Typography>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(145px, 1fr))", gap: 1.5 }}>
                    {extractedImages.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ gridColumn: "1 / -1", mb: -0.5 }}>
                        Drag an image up to the Image section to assign it to this question.
                      </Typography>
                    )}
                    {extractedImages.map((img, idx) => {
                      const isThisQuestion = img.assigned_question_id != null && String(img.assigned_question_id) === String(id);
                      const assignedQNumber = img.assigned_question_id != null
                        ? (qLabelMap[String(img.assigned_question_id)] ?? null)
                        : null;
                      return (
                        <Box
                          key={idx}
                          draggable={!isThisQuestion}
                          onDragStart={() => { draggedExtractedImageRef.current = img; }}
                          onDragEnd={() => { if (draggedExtractedImageRef.current === img) draggedExtractedImageRef.current = null; }}
                          sx={{
                            border: "1px solid",
                            borderColor: isThisQuestion ? "primary.light" : img.assigned_question_id ? "success.light" : "warning.light",
                            borderRadius: 2,
                            p: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                            bgcolor: isThisQuestion
                              ? "rgba(99,102,241,0.05)"
                              : img.assigned_question_id
                              ? "rgba(76,175,80,0.04)"
                              : "rgba(255,152,0,0.06)",
                            cursor: isThisQuestion ? "default" : "grab",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              height: 90,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              borderRadius: 1,
                              bgcolor: "#f5f5f5",
                            }}
                          >
                            <img
                              src={img.url}
                              alt={img.filename}
                              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.65rem",
                              color: "text.secondary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={img.filename}
                          >
                            {img.filename}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              isThisQuestion
                                ? "This question"
                                : img.assigned_question_id
                                ? assignedQNumber != null
                                  ? assignedQNumber
                                  : String(img.assigned_question_id)
                                : "Unassigned"
                            }
                            color={isThisQuestion ? "primary" : img.assigned_question_id ? "success" : "warning"}
                            variant="filled"
                            sx={{ fontSize: "0.6rem", height: 18, fontWeight: 700 }}
                          />
                          {/* Assign to this question — only when not already assigned here */}
                          {!isThisQuestion && (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={reassigningImage === img.url || removingExtractedImage === img.url}
                              onClick={() => handleAssignToThisQuestion(img)}
                              startIcon={reassigningImage === img.url ? <CircularProgress size={12} color="inherit" /> : null}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.7rem",
                                py: 0.25,
                                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" },
                              }}
                            >
                              {reassigningImage === img.url ? "Assigning…" : "Assign Here"}
                            </Button>
                          )}

                          {/* Reassign to a different question via dialog */}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={reassigningImage === img.url || removingExtractedImage === img.url}
                            onClick={() => openReassignDialog(img)}
                            sx={{ textTransform: "none", fontSize: "0.7rem", py: 0.25 }}
                          >
                            Reassign
                          </Button>

                          {/* Remove / delete the image from the import */}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={removingExtractedImage === img.url || reassigningImage === img.url}
                            onClick={() => handleRemoveExtractedImage(img)}
                            startIcon={removingExtractedImage === img.url ? <CircularProgress size={12} color="inherit" /> : <Delete sx={{ fontSize: "0.75rem !important" }} />}
                            sx={{ textTransform: "none", fontSize: "0.7rem", py: 0.25 }}
                          >
                            {removingExtractedImage === img.url ? "Removing…" : "Remove"}
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}

        {/* Type-specific section — hidden for grouped (handled below) */}
        {type !== "grouped" && (
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2, background: "#fff" }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                Answer Configuration
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleTypeChange("grouped")}
                  sx={{
                    borderColor: "#6366f1",
                    color: "#6366f1",
                    textTransform: "none",
                    "&:hover": { borderColor: "#4f46e5", background: "rgba(99,102,241,0.04)" },
                  }}
                  title="Keep the question text as the passage/stem and add sub-questions beneath it"
                >
                  Convert to sub-questions
                </Button>
                {isEdit && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={isGeneratingAnswer ? <CircularProgress size={16} /> : <AutoAwesome />}
                    onClick={handleGenerateAnswer}
                    disabled={isGeneratingAnswer}
                    sx={{
                      borderColor: "#8b5cf6",
                      color: "#8b5cf6",
                      "&:hover": { borderColor: "#7c3aed", background: "rgba(139, 92, 246, 0.04)" },
                    }}
                  >
                    {isGeneratingAnswer ? "Generating..." : "AI Generate Answer"}
                  </Button>
                )}
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {renderTypeSpecificFields()}
            {type !== "grouped" ? (() => {
              const auto = forceMeasurementTool === "auto" ? describeMeasurementAutoDetect({ question_text: questionText, type }) : null;
              const toolLabel = { angle: "Protractor", length: "Ruler", construct: "Full construction set" };
              return (
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.5, maxWidth: 460 }}>
                  <FormControl size="small" sx={{ maxWidth: 340 }}>
                    <InputLabel>Measurement tool for student</InputLabel>
                    <Select
                      label="Measurement tool for student"
                      value={forceMeasurementTool}
                      onChange={(e) => setForceMeasurementTool(e.target.value)}
                    >
                      <MenuItem value="auto">Auto (detect from question text)</MenuItem>
                      <MenuItem value="none">None (no measurement tools)</MenuItem>
                      <MenuItem value="angle">Protractor only (angle)</MenuItem>
                      <MenuItem value="length">Ruler only (length)</MenuItem>
                      <MenuItem value="construct">Full construction set (protractor + ruler + compass + set square)</MenuItem>
                    </Select>
                  </FormControl>
                  {forceMeasurementTool === "auto" ? (
                    <Typography variant="caption" color={auto ? "primary.main" : "text.secondary"}>
                      {auto
                        ? `Auto-detected: ${toolLabel[auto.mode] || auto.mode}${auto.phrase ? ` — matched "${auto.phrase}"` : ""}. Pick a tool above to override.`
                        : "Auto: no measurement tool detected from the question text. Pick a tool above to force one."}
                    </Typography>
                  ) : null}
                </Box>
              );
            })() : null}
          </Paper>
        )}

        {/* Sub-questions section — only for grouped type */}
        {type === "grouped" && (
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, border: "1px solid #c7d2fe", borderRadius: 2, background: "#fff" }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                  Sub-questions
                </Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Each sub-question shares the stem above.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ToggleButtonGroup
                  value={subLabelType}
                  exclusive
                  size="small"
                  onChange={(_, val) => { if (val) setSubLabelType(val); }}
                  sx={{ height: 32 }}
                >
                  <ToggleButton value="letter" sx={{ px: 1.5, fontSize: "0.75rem", textTransform: "none" }}>a, b, c</ToggleButton>
                  <ToggleButton value="roman" sx={{ px: 1.5, fontSize: "0.75rem", textTransform: "none" }}>i, ii, iii</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setSubQuestions((prev) => [...prev, defaultSubQuestion()])}
                  sx={{ borderColor: "#6366f1", color: "#6366f1", textTransform: "none" }}
                >
                  Add Sub-question
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupedMarksMode === "fixed"}
                    onChange={(e) => setGroupedMarksMode(e.target.checked ? "fixed" : "sum")}
                    size="small"
                  />
                }
                label={<Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>Fixed total marks (fractional)</Typography>}
                sx={{ m: 0 }}
              />
              {groupedMarksMode === "fixed" && (
                <TextField
                  size="small"
                  label="Total marks"
                  type="number"
                  value={groupedFixedTotal}
                  onChange={(e) => setGroupedFixedTotal(e.target.value)}
                  inputProps={{ min: 1 }}
                  sx={{ width: 130 }}
                  helperText={subQuestions.length > 0 ? `${groupedFixedTotal}/${subQuestions.length} per part` : ""}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupedExtend}
                    onChange={(e) => setGroupedExtend(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>Show all parts extended (one question) — for plotting/drawing on a shared grid</Typography>}
                sx={{ m: 0 }}
              />
              <UnifySharedGridsControl
                subQuestions={subQuestions}
                setSubQuestions={setSubQuestions}
                labelOf={(i) => (subLabelType === "roman" ? toRoman(i + 1) : String.fromCharCode(97 + i))}
              />
            </Box>

            <SharedGridPartsEditor
              subQuestions={subQuestions}
              setSubQuestions={setSubQuestions}
              labelOf={(i) => (subLabelType === "roman" ? toRoman(i + 1) : String.fromCharCode(97 + i))}
            />

            {subQuestions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                No sub-questions yet. Click "Add Sub-question" to start.
              </Typography>
            )}

            <DndContext sensors={subDndSensors} collisionDetection={closestCenter} onDragEnd={handleSubDragEnd}>
              <SortableContext items={subQuestions.map((sq) => sq._key)} strategy={verticalListSortingStrategy}>
                {subQuestions.map((sq, sqIdx) => (
                  <SortableSubQuestionItem key={sq._key} id={sq._key}>
                    <SubQuestionEditor
                      sq={sq}
                      sqIndex={sqIdx}
                      label={subLabelType === "roman" ? toRoman(sqIdx + 1) : String.fromCharCode(97 + sqIdx)}
                      parentImages={imageUrls}
                      parentQuestionId={id}
                      isEdit={isEdit}
                      parentDifficulty={difficulty}
                      parentTopic={topic}
                      fixedMarksLabel={groupedMarksMode === "fixed" ? `${parseInt(groupedFixedTotal, 10) || 1}/${subQuestions.length}` : null}
                      onChange={(updates) =>
                        setSubQuestions((prev) =>
                          prev.map((s, i) => (i === sqIdx ? { ...s, ...updates } : s))
                        )
                      }
                      onDelete={() =>
                        setSubQuestions((prev) => prev.filter((_, i) => i !== sqIdx))
                      }
                    />
                  </SortableSubQuestionItem>
                ))}
              </SortableContext>
            </DndContext>

            {subQuestions.length > 0 && (
              <Box sx={{ mt: 1, textAlign: "right" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setSubQuestions((prev) => [...prev, defaultSubQuestion()])}
                  sx={{ borderColor: "#6366f1", color: "#6366f1", textTransform: "none" }}
                >
                  Add Sub-question
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* Explanation Section */}
        <Paper
          elevation={0}
          sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2, background: "#fff" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Explanation (optional)
            </Typography>
            {isEdit && id && type !== "grouped" && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={generatingExplanation ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: "0.9rem !important" }} />}
                  onClick={handleGenerateExplanationAi}
                  disabled={generatingExplanation || improving}
                  sx={{ py: 0.25, px: 1, fontSize: "0.75rem", textTransform: "none" }}
                >
                  {generatingExplanation ? "Generating…" : "AI Generate"}
                </Button>
              </Box>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              "& .ql-toolbar": { borderRadius: "6px 6px 0 0" },
              "& .ql-container": { minHeight: 120, borderRadius: "0 0 6px 6px", fontSize: "0.9rem" },
            }}
          >
            <QuillR2Editor
              value={explanation}
              onChange={(html) => setExplanation(normalizeQuillValue(html))}
              modules={EXPL_QUILL_MODULES}
              formats={EXPL_QUILL_FORMATS}
              placeholder="Explain the correct answer for student review..."
              style={{ minHeight: 160 }}
              showMathTools
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 7 }}>
            Shown to students after they complete the exam. Use the image button to embed explanation images.
          </Typography>
        </Paper>

        {/* Video Explanation Section - Only show when editing */}
        {isEdit && (
          <Box sx={{ mb: 3 }}>
            <VideoExplanationPanel
              questionId={id}
              initialData={videoData}
              onStatusChange={(data) => setVideoData(data)}
            />
          </Box>
        )}

        {/* Submit */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={() => navigate(returnTo)}
            sx={{
              borderColor: "#999",
              color: "#666",
              "&:hover": {
                borderColor: "#666",
                background: "rgba(0,0,0,0.04)",
              }
            }}
          >
            Cancel
          </Button>
          {isEdit && importId && (
            <Button
              variant="outlined"
              startIcon={applyingChanges ? <CircularProgress size={20} color="inherit" /> : <Sync />}
              onClick={handleApplyChanges}
              disabled={applyingChanges || saving}
              sx={{
                borderColor: "#0ea5e9",
                color: "#0ea5e9",
                "&:hover": { borderColor: "#0284c7", background: "rgba(14,165,233,0.04)" },
              }}
            >
              {applyingChanges
                ? "Applying…"
                : applyChangesResult
                  ? (() => {
                      const r = applyChangesResult;
                      const items = [
                        (r.meta_updated ?? 0) > 0 && { n: r.meta_updated, label: "type" },
                        (r.image_fixed ?? 0) > 0 && { n: r.image_fixed, label: "img" },
                        (r.diagrams_generated ?? 0) > 0 && { n: r.diagrams_generated, label: "svg" },
                        (r.text_updated ?? 0) > 0 && { n: r.text_updated, label: "txt" },
                        (r.answer_data_updated ?? 0) > 0 && { n: r.answer_data_updated, label: "ans" },
                        (r.decimal_fixed ?? 0) > 0 && { n: r.decimal_fixed, label: "dec" },
                        (r.options_fixed ?? 0) > 0 && { n: r.options_fixed, label: "opts" },
                      ].filter(Boolean);
                      if (items.length === 0) return "✓ up-to-date";
                      return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                          <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 700, color: "success.main", mr: 0.25 }}>✓</Typography>
                          {items.map(({ n, label }, i) => (
                            <Box key={i} sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                              <Box component="span" sx={{ bgcolor: "#1a1a1a", color: "#fff", fontWeight: 700, fontSize: "0.65rem", fontFamily: "monospace", px: 0.6, py: 0.1, borderRadius: 0.4, letterSpacing: "0.03em" }}>{n}</Box>
                              <Typography component="span" sx={{ fontSize: "0.65rem", color: "text.secondary" }}>{label}</Typography>
                              {i < items.length - 1 && <Typography component="span" sx={{ fontSize: "0.65rem", color: "text.disabled", mx: 0.15 }}>·</Typography>}
                            </Box>
                          ))}
                        </Box>
                      );
                    })()
                  : "Apply Changes"}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
              },
              px: 4,
              py: 1,
              boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
            }}
          >
            {saving ? "Saving..." : isEdit ? "Update Question" : "Create Question"}
          </Button>
        </Box>
      </form>

      {/* ── Reassign Image Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={reassignDialogOpen}
        onClose={() => !reassigningImage && setReassignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reassign Image</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {reassignTargetImage && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <img
                  src={reassignTargetImage.url}
                  alt="to reassign"
                  style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                />
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              Select a question to assign this image to, or leave blank to unassign it from all questions.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Assign to question</InputLabel>
              <Select
                value={reassignSelectedQuestion}
                label="Assign to question"
                onChange={(e) => setReassignSelectedQuestion(e.target.value)}
              >
                <MenuItem value=""><em>Unassigned (remove from all questions)</em></MenuItem>
                {imagesPanelQuestions.map((q) => (
                  <MenuItem key={String(q.id)} value={String(q.id)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: "100%", overflow: "hidden" }}>
                      <Chip label={qLabelMap[String(q.id)] ?? ''} size="small" sx={{ fontSize: "0.65rem", height: 18, flexShrink: 0, fontWeight: 700, bgcolor: "#1a1a1a", color: "#fff" }} />
                      <Chip label={q.type} size="small" sx={{ fontSize: "0.65rem", height: 18, flexShrink: 0 }} variant="outlined" />
                      <Typography
                        variant="body2"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.82rem" }}
                      >
                        {q.label || "(no text)"}
                        {String(q.id) === String(id) && (
                          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "primary.main", fontWeight: 700 }}>
                            ← this question
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignDialogOpen(false)} disabled={!!reassigningImage}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReassignConfirm}
            disabled={!!reassigningImage}
            startIcon={reassigningImage ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none" }}
          >
            {reassigningImage ? "Saving…" : "Confirm Reassign"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={aiImproveOpen} onClose={() => setAiImproveOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>AI suggestion — compare and accept</DialogTitle>
        <DialogContent dividers>
          <AiAcceptSections value={aiAccept} onChange={setAiAccept} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                Original
              </Typography>
              {aiOriginal && (
                <>
                  <QuestionPaperLayout
                    question={aiOriginal}
                    questionNumber={1}
                    showAnswers
                    showAdminHints={false}
                  />
                  <AiExplanationPreview html={aiOriginal.explanation} />
                </>
              )}
            </Paper>
            <Paper
              variant="outlined"
              sx={{ p: 2, border: "2px solid", borderColor: "secondary.main" }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, color: "secondary.main" }}>
                AI suggestion — preview of what will be applied
              </Typography>
              {aiSuggestion && (() => {
                const merged = mergeAiSuggestion(aiOriginal, aiSuggestion, aiAccept);
                return (
                  <>
                    <QuestionPaperLayout
                      question={merged}
                      questionNumber={1}
                      showAnswers
                      showAdminHints={false}
                    />
                    <AiExplanationPreview html={merged.explanation} />
                  </>
                );
              })()}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiImproveOpen(false)}>Dismiss</Button>
          <Button
            variant="contained"
            onClick={() => {
              applyImprovedQuestionToForm(mergeAiSuggestion(aiOriginal, aiSuggestion, aiAccept));
              setAiImproveOpen(false);
              toast.success("Applied AI suggestion to the form. Review and save when ready.");
            }}
            disabled={!aiSuggestion || !Object.values(aiAccept).some(Boolean)}
            sx={{ textTransform: "none" }}
          >
            Accept and apply
          </Button>
        </DialogActions>
      </Dialog>

      <ImportMarkdownViewerDialog
        open={markdownDialogOpen}
        importId={importId ? Number(importId) : null}
        filename=""
        onClose={() => setMarkdownDialogOpen(false)}
      />
    </Box>
  );
};

export default QuestionBankForm;
